import React, { useState, useEffect, useRef } from 'react';
import axios, { all } from 'axios';
import { header } from '../components/header.encry';
import Select from 'react-select';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDolibarrToken } from '../utils/authStorage';
import { processCmiExcelFile } from '../utils/cmiExcelProcessor';
import { processVpsExcelFile, postVpsTransactions, groupVpsTransactionsByDate, groupVpsDebitTransactionsByTable } from '../utils/vpsExcelProcessor';
import * as XLSX from 'xlsx';
import CompanyService from '../components/CompanyService';

const DEFAULT_TVA = 20;
const VPS_OFLOREST_SOCID = 31;
function CmiAccounting(props) {

    const [key, setKey] = useState(0);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [facture, setFacture] = useState(null);
    const [vpsSummary, setVpsSummary] = useState({});
    const [vpsDebitGroups, setVpsDebitGroups] = useState([]);
    const [vpsActiveTab, setVpsActiveTab] = useState('credits');
    const [selectedSystem, setSelectedSystem] = useState('cmi'); // 'cmi' or 'vps'
    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
        setVpsSummary({});
        setVpsDebitGroups([]);
        setVpsActiveTab('credits');
    };
    const [selectAll, setSelectAll] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [chosenCompany, setChosenCompany] = useState()
    const [companyOptions, setCompanyOptions] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]); // Track which rows are expanded
    
    // Wizard states
    const [wizardTransaction, setWizardTransaction] = useState(null);
    const [wizardStep, setWizardStep] = useState('confirm'); // confirm, processing, invoice
    const [wizardProcessing, setWizardProcessing] = useState(false);
    const [processedInvoiceId, setProcessedInvoiceId] = useState(null);
    const [wizardList, setWizardList] = useState([]); // List of transactions to process
    const [wizardCurrentIndex, setWizardCurrentIndex] = useState(0); // Current position in the list
    const [lastProcessedInWizard, setLastProcessedInWizard] = useState(null); // Track last processed transaction in wizard session
    const [wizardShowDetails, setWizardShowDetails] = useState(false); // Track if transaction details are shown in wizard
    const [reglementDateModal, setReglementDateModal] = useState({ open: false, invoiceId: null, minDate: '', value: '' });
    const reglementDateResolverRef = useRef(null);

    const roundCurrency = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
    const formatCurrency = (value) => `${roundCurrency(value || 0).toFixed(2)} MAD`;
    const hasVpsPaidSummary = Number.isFinite(Number(vpsSummary?.netMarchand));

    const handleCheckboxChange = (remiseId) => {
        // Toggle the selection state of the order private note
        setSelectedOrders((prevSelectedOrders) => {
            if (prevSelectedOrders.includes(remiseId)) {
                return prevSelectedOrders.filter((order) => order !== remiseId);
            } else {
                return [...prevSelectedOrders, remiseId];
            }
        });

    };

    const toggleRowExpansion = (remiseId) => {
        setExpandedRows((prevExpandedRows) => {
            if (prevExpandedRows.includes(remiseId)) {
                return prevExpandedRows.filter((id) => id !== remiseId);
            } else {
                return [...prevExpandedRows, remiseId];
            }
        });
    };
    const normalizeCompanyName = (value) => String(value || '').trim().toLowerCase();
    const getClientOptions = () => {
        try {
            const parsed = JSON.parse(process.env?.REACT_APP_CLIENTS || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.warn('Unable to parse REACT_APP_CLIENTS', error);
            return [];
        }
    };

    useEffect(() => {
        const loadCompanies = async () => {
            try {
                const response = await CompanyService.fetchCompanies();
                const companies = response?.response || response || [];
                const clients = getClientOptions();
                const matchedOptions = (Array.isArray(companies) ? companies : [])
                    .map((company) => {
                        const companyName = String(company?.name || '').trim();
                        const normalizedName = normalizeCompanyName(companyName);
                        const matchedClient = clients.find((client) => normalizeCompanyName(client?.name) === normalizedName);

                        if (!companyName || !matchedClient?.name) {
                            return null;
                        }

                        return {
                            label: companyName,
                            value: matchedClient.name,
                            companyId: company?._id || company?.id || null
                        };
                    })
                    .filter(Boolean);

                setCompanyOptions(matchedOptions);
            } catch (error) {
                console.error('Unable to fetch companies for CMI Accounting', error);
                toast.error('Impossible de charger les sociétés.');
                setCompanyOptions([]);
            }
        };

        loadCompanies();
    }, []);

    const getParams = () => {
        return getClientOptions().find(x => normalizeCompanyName(x?.name) === normalizeCompanyName(chosenCompany?.value));
    }
    const isOfloristSelected = () => normalizeCompanyName(chosenCompany?.value) === 'oflorist';
    const isSistersFlowersSelected = () => normalizeCompanyName(chosenCompany?.value) === 'sisters flowers';

    const normalizeHeaderKey = (value) => {
        return String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase();
    };

    const parseNumericValue = (value) => {
        if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
        if (typeof value !== 'string') return NaN;
        const normalized = value.replace(/\s/g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : NaN;
    };

    const normalizeRate = (rawValue) => {
        const value = Number(rawValue);
        if (!Number.isFinite(value) || value <= 0) return NaN;
        if (value <= 1) return value;      // 0.15 => 15%
        if (value <= 100) return value / 100; // 15 => 15%
        return NaN; // Probably an absolute amount, not a rate
    };

    const getRandomVpsProductLabel = () => {
        const labels = ['Chocolat', 'Plante', 'Pot', 'Fleurs Coupée', 'Composition de plantes'];
        return labels[Math.floor(Math.random() * labels.length)];
    };

    const toDdMmYyyy = (date) => {
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}/${m}/${y}`;
    };

    const getTransactionTotal = (trx) => {
        if (!trx || !Array.isArray(trx.lines) || trx.lines.length === 0) return 0;
        if (trx.isGlovo) {
            return Number(trx?.glovoSummary?.baseCommissionTtc) || 0;
        }
        if (trx.isCarrefour) {
            return trx.lines.reduce((acc, line) => acc + (Number(line?.ca_ht) || 0), 0);
        }
        if (trx.isVps && Array.isArray(trx.rawTransactions) && trx.rawTransactions.length > 0) {
            return trx.rawTransactions.reduce((acc, transaction) => acc + (Number(transaction?.montantNetTTC) || 0), 0);
        }
        return trx.lines.reduce((acc, line) => acc + ((Number(line?.subprice) || 0) * (Number(line?.qty) || 1)), 0);
    };

    const normalizeVpsKeyPart = (value) => String(value ?? '').trim().toUpperCase();

    const buildVpsTransactionKey = (transaction = {}) => [
        normalizeVpsKeyPart(transaction.authorisationReference),
        normalizeVpsKeyPart(transaction.transaction_direction || transaction.transactionDirection || 'credit'),
        normalizeVpsKeyPart(transaction.authorisationDate),
        normalizeVpsKeyPart(transaction.hiddenCardNumber),
        normalizeVpsKeyPart(transaction.stan),
        normalizeVpsKeyPart(transaction.authorisationAmount),
        normalizeVpsKeyPart(transaction.issuerBankAbbreviation)
    ].join('|');

    const mergeVpsTransactionsWithBackend = (processedData = [], response = {}) => {
        const backendTransactions = [
            ...(response.transactions || []),
            ...(response.duplicateDetails || [])
        ].filter(Boolean);
        const backendByKey = new Map(
            backendTransactions.map((transaction) => [buildVpsTransactionKey(transaction), transaction])
        );

        return processedData.map((localTransaction) => {
            const backendTransaction = backendByKey.get(buildVpsTransactionKey(localTransaction));
            if (!backendTransaction) return localTransaction;

            return {
                ...localTransaction,
                ...backendTransaction,
                merchantCredit: localTransaction.merchantCredit ?? backendTransaction.merchant_credit,
                merchantDebit: localTransaction.merchantDebit ?? backendTransaction.merchant_debit,
                commissionRate: localTransaction.commissionRate ?? backendTransaction.commission_rate,
                commission: localTransaction.commission ?? backendTransaction.commission_ht,
                commissionWithTax: localTransaction.commissionWithTax ?? backendTransaction.commission_ttc,
                transaction_direction: localTransaction.transaction_direction || backendTransaction.transaction_direction,
                transactionDirection: localTransaction.transactionDirection || backendTransaction.transaction_direction
            };
        });
    };

    const getDisplayTotalAmount = () => {
        if (selectedSystem === 'vps' && Number.isFinite(Number(vpsSummary?.totalCreditMerchant))) {
            return Number(vpsSummary.totalCreditMerchant);
        }
        return facture?.reduce((acc, item) => acc + getTransactionTotal(item), 0) || 0;
    };

    const getTransactionType = (transaction) => {
        if (transaction?.isCarrefour) return 'carrefour';
        if (transaction?.isGlovo) return 'glovo';
        if (transaction?.isVps) return 'vps';
        return 'cmi';
    };

    const getVpsStage = (transaction) => {
        if (!transaction?.isVps) return '';
        if (transaction?.invoiceNumber) return 'validée';
        if (transaction?.invoiceId) return 'créée';
        if (transaction?.stage) return String(transaction.stage);
        if (transaction?.processed) return 'traitée';
        return 'en attente';
    };

    const isVpsEffectivelyProcessed = (transaction) => {
        if (!transaction?.isVps) return Boolean(transaction?.processed);
        return Boolean(
            transaction?.processed ||
            transaction?.invoiceId ||
            transaction?.invoiceNumber ||
            transaction?.stage
        );
    };

    const isVpsGreenRow = (transaction) => {
        if (!transaction?.isVps) return Boolean(transaction?.processed);
        return Boolean(transaction?.invoiceNumber || String(transaction?.stage || '').toLowerCase() === 'validée');
    };

    const sumGlovoAdBudgetEntries = (entries, fallbackValue = 0) => {
        if (Array.isArray(entries) && entries.length > 0) {
            return roundCurrency(entries.reduce((sum, entry) => sum + (parseNumericValue(entry) || 0), 0));
        }
        return roundCurrency(Number(fallbackValue) || 0);
    };

    const getGlovoAdBudgetEntries = (transaction) => {
        const entries = transaction?.glovoSummary?.adBudgetEntries;
        if (Array.isArray(entries) && entries.length > 0) {
            return entries;
        }

        const fallback = Number(transaction?.glovoSummary?.adBudgetHt) || 0;
        return fallback > 0 ? [String(fallback)] : [''];
    };

    const calculateGlovoFinancials = (transaction) => {
        const baseCommissionTtc = Number(transaction?.glovoSummary?.baseCommissionTtc) || 0;
        const commissionBilledHt = Number(transaction?.glovoSummary?.commissionBilledHt) || 0;
        const commissionRate = Number(transaction?.glovoSummary?.commissionRate) || 0;
        const adBudgetHt = sumGlovoAdBudgetEntries(
            transaction?.glovoSummary?.adBudgetEntries,
            transaction?.glovoSummary?.adBudgetHt
        );
        const disputeTtc = Number(transaction?.glovoSummary?.disputeTtc) || 0;
        const aTtc = roundCurrency((commissionBilledHt + adBudgetHt) * 1.2);
        const bTtc = roundCurrency(baseCommissionTtc - aTtc);

        return {
            baseCommissionTtc,
            commissionBilledHt,
            commissionRate,
            adBudgetHt,
            disputeTtc,
            aTtc,
            bTtc
        };
    };

    const getGlovoSocid = (companyName) => {
        const normalizedCompanyName = normalizeCompanyName(companyName);
        if (normalizedCompanyName === 'oflorist') return 66;
        if (normalizedCompanyName === 'sisters flowers') return 9;
        return Number(getParams()?.site || 0) || null;
    };

    const getGlovoPaymentAccountId = (storeAddressId) => {
        if (isSistersFlowersSelected()) {
            return 1;
        }
        return String(storeAddressId || '').trim() === '855170' ? 11 : 4;
    };

    const getCmiBankAccountId = (transaction) => {
        if (isSistersFlowersSelected()) {
            return "1";
        }

        return (getParams()?.site == transaction?.socid && isOfloristSelected()) ? "11" : "4";
    };

    const buildGlovoPrivateNote = (transaction) => {
        const totals = calculateGlovoFinancials(transaction);
        const baseLabel = String(transaction?.note_private || 'Glovo').trim();
        const invoiceFileNumber = String(transaction?.glovoSummary?.invoiceFileNumber || '').trim();
        const rateLabel = Number.isFinite(totals.commissionRate) && totals.commissionRate > 0
            ? `${roundCurrency(totals.commissionRate * 100).toFixed(2)}%`
            : '0.00%';

        return [
            baseLabel,
            invoiceFileNumber ? `Invoice file number: ${invoiceFileNumber}` : null,
            `Base commission TTC: ${totals.baseCommissionTtc.toFixed(2)} MAD`,
            `Commission facturee HT + Ad budget HT: ${(totals.commissionBilledHt + totals.adBudgetHt).toFixed(2)} MAD`,
            `Commission facturee HT: ${totals.commissionBilledHt.toFixed(2)} MAD`,
            `Pourcentage commission HT: ${rateLabel}`,
            `Ad budget HT: ${totals.adBudgetHt.toFixed(2)} MAD`,
            `Dispute TTC: ${totals.disputeTtc.toFixed(2)} MAD`,
            `A TTC: ${totals.aTtc.toFixed(2)} MAD`,
            `B TTC (hors dispute): ${totals.bTtc.toFixed(2)} MAD`
        ].filter(Boolean).join(' | ');
    };

    const parseCsvLine = (line, delimiter = ';') => {
        const values = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i += 1) {
            const char = line[i];
            const next = line[i + 1];

            if (char === '"') {
                if (inQuotes && next === '"') {
                    current += '"';
                    i += 1;
                } else {
                    inQuotes = !inQuotes;
                }
                continue;
            }

            if (char === delimiter && !inQuotes) {
                values.push(current);
                current = '';
                continue;
            }

            current += char;
        }

        values.push(current);
        return values.map((value) => String(value || '').trim());
    };

    const parseIsoLikeDate = (value) => {
        const raw = String(value || '').trim();
        if (!raw) return null;

        const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
            const dt = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
            return Number.isNaN(dt.getTime()) ? null : dt;
        }

        return parseExcelDate(raw);
    };

    const parseDateFromFileName = (fileName) => {
        const match = String(fileName || '').match(/(\d{2})(\d{2})(\d{2})/);
        if (!match) return null;
        const dt = new Date(Number(`20${match[3]}`), Number(match[2]) - 1, Number(match[1]));
        return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const splitGlovoDescription = (description) => {
        const source = String(description || '').replace(/\s+/g, ' ').trim();
        if (!source) return [];

        const normalizedSource = source.replace(/(\d{4})(\d{1,2}\s*x\s)/g, '$1 $2');
        const matches = Array.from(normalizedSource.matchAll(/(\d+(?:[.,]\d+)?)\s*x\s*/gi));
        if (!matches.length) {
            return [{
                qty: 1,
                label: normalizedSource
            }];
        }

        return matches.map((match, index) => {
            const start = match.index || 0;
            const end = index + 1 < matches.length ? (matches[index + 1].index || normalizedSource.length) : normalizedSource.length;
            const segment = normalizedSource.slice(start, end).trim();
            const qty = parseNumericValue(String(match[1]).replace(',', '.'));
            return {
                qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
                label: segment
            };
        }).filter((item) => item.label);
    };

    const parseGlovoCsvFile = async (file) => {
        const text = await file.text();
        const normalizedText = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const allLines = normalizedText.split('\n').map((line) => line.trimEnd()).filter(Boolean);
        if (allLines.length < 2) {
            throw new Error('Fichier Glovo vide ou invalide.');
        }

        const headerRow = parseCsvLine(allLines[0], ';');
        const rows = allLines.slice(1).map((line) => parseCsvLine(line, ';'));

        const getColumnIndex = (columnName) => headerRow.findIndex((value) => normalizeHeaderKey(value) === normalizeHeaderKey(columnName));
        const columnIndexes = {
            storeAddressId: getColumnIndex('Store Address Id'),
            storeName: getColumnIndex('Nom de SAID'),
            orderDate: getColumnIndex('Date de commande'),
            orderId: getColumnIndex('ID de commande'),
            orderCode: getColumnIndex('Code de commande'),
            description: getColumnIndex('Description de la commande'),
            baseCommission: getColumnIndex('Base de commission (produits-promotions)'),
            promotionCommission: getColumnIndex('Commission de promotion'),
            commissionBilled: getColumnIndex('Commission facturee'),
            commissionRate: getColumnIndex('Pourcentage de commission')
        };

        if (Object.values(columnIndexes).some((idx) => idx === -1)) {
            throw new Error('Colonnes Glovo introuvables dans le CSV.');
        }

        const orderRows = rows.filter((row) => {
            const baseCommission = parseNumericValue(row[columnIndexes.baseCommission]);
            const description = String(row[columnIndexes.description] || '').trim();
            return Number.isFinite(baseCommission) && baseCommission > 0 && description;
        });

        if (!orderRows.length) {
            throw new Error('Aucune ligne de commande Glovo exploitable trouvée.');
        }

        const fileDate = parseDateFromFileName(file.name);
        const datedRows = orderRows
            .map((row) => parseIsoLikeDate(row[columnIndexes.orderDate]))
            .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()));
        const effectiveDate = fileDate || datedRows[datedRows.length - 1] || new Date();
        const periodCompact = `${String(effectiveDate.getMonth() + 1).padStart(2, '0')}${effectiveDate.getFullYear()}`;
        const storeName = String(orderRows[0][columnIndexes.storeName] || chosenCompany?.label || 'Glovo').trim();
        const storeAddressId = String(orderRows[0][columnIndexes.storeAddressId] || '').trim();
        const commissionRate = orderRows
            .map((row) => normalizeRate(parseNumericValue(row[columnIndexes.commissionRate])))
            .find((value) => Number.isFinite(value) && value > 0) || 0;
        const baseCommissionTtc = roundCurrency(orderRows.reduce((sum, row) => sum + (parseNumericValue(row[columnIndexes.baseCommission]) || 0), 0));
        const commissionBilledHt = roundCurrency(orderRows.reduce((sum, row) => sum + (parseNumericValue(row[columnIndexes.commissionBilled]) || 0), 0));
        const adBudgetHt = columnIndexes.promotionCommission === -1
            ? 0
            : roundCurrency(orderRows.reduce((sum, row) => sum + (parseNumericValue(row[columnIndexes.promotionCommission]) || 0), 0));

        const lines = [];
        orderRows.forEach((row, rowIndex) => {
            const description = row[columnIndexes.description];
            const baseCommissionRowTtc = parseNumericValue(row[columnIndexes.baseCommission]) || 0;
            if (baseCommissionRowTtc <= 0) return;

            const parts = splitGlovoDescription(description);
            const totalQty = parts.reduce((sum, part) => sum + (Number(part.qty) || 0), 0) || 1;
            const totalHt = roundCurrency(baseCommissionRowTtc / 1.2);
            let remainingHt = totalHt;

            parts.forEach((part, partIndex) => {
                const qty = Number(part.qty) || 1;
                const allocatedHt = partIndex === parts.length - 1
                    ? roundCurrency(remainingHt)
                    : roundCurrency(totalHt * (qty / totalQty));
                remainingHt = roundCurrency(remainingHt - allocatedHt);

                lines.push({
                    multicurrency_code: 'MAD',
                    label: part.label,
                    desc: part.label,
                    tva_tx: DEFAULT_TVA,
                    qty,
                    subprice: roundCurrency(allocatedHt / qty),
                    ca_ht: allocatedHt,
                    product_type: 'product',
                    trx: qty,
                    orderId: row[columnIndexes.orderId] || row[columnIndexes.orderCode] || `row-${rowIndex + 1}`
                });
            });
        });

        if (!lines.length) {
            throw new Error('Impossible de générer les lignes de facture Glovo.');
        }

        const fileBaseName = String(file.name || 'glovo').replace(/\.[^.]+$/, '');
        const socid = getGlovoSocid(chosenCompany?.value);

        return [{
            socid,
            fk_account: socid,
            datef: toDdMmYyyy(effectiveDate),
            remiseId: `GLOVO_${storeAddressId || 'UNKNOWN'}_${periodCompact}_${fileBaseName}`,
            note_private: `Glovo ${storeName} ${periodCompact}`.trim(),
            lines,
            company: chosenCompany?.label || chosenCompany?.value || 'Glovo',
            processed: false,
            isGlovo: true,
            glovoSummary: {
                storeAddressId,
                storeName,
                baseCommissionTtc,
                commissionBilledHt,
                commissionRate,
                adBudgetHt,
                adBudgetEntries: adBudgetHt > 0 ? [String(adBudgetHt)] : [''],
                disputeTtc: 0,
                fileName: file.name,
                invoiceFileNumber: fileBaseName
            }
        }];
    };

    const updateGlovoTransaction = (remiseId, field, rawValue) => {
        const parsed = parseNumericValue(rawValue);
        const nextValue = Number.isFinite(parsed) ? roundCurrency(parsed) : 0;

        const patchTransaction = (transaction) => {
            if (!transaction || transaction.remiseId !== remiseId || !transaction.isGlovo) return transaction;
            return {
                ...transaction,
                glovoSummary: {
                    ...transaction.glovoSummary,
                    [field]: nextValue
                }
            };
        };

        setFacture((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardList((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardTransaction((prev) => patchTransaction(prev));
    };

    const updateGlovoBudgetEntry = (remiseId, index, rawValue) => {
        const patchTransaction = (transaction) => {
            if (!transaction || transaction.remiseId !== remiseId || !transaction.isGlovo) return transaction;
            const currentEntries = getGlovoAdBudgetEntries(transaction);
            const nextEntries = currentEntries.map((entry, entryIndex) => (entryIndex === index ? rawValue : entry));
            return {
                ...transaction,
                glovoSummary: {
                    ...transaction.glovoSummary,
                    adBudgetEntries: nextEntries,
                    adBudgetHt: sumGlovoAdBudgetEntries(nextEntries)
                }
            };
        };

        setFacture((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardList((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardTransaction((prev) => patchTransaction(prev));
    };

    const addGlovoBudgetEntry = (remiseId) => {
        const patchTransaction = (transaction) => {
            if (!transaction || transaction.remiseId !== remiseId || !transaction.isGlovo) return transaction;
            const currentEntries = getGlovoAdBudgetEntries(transaction);
            const nextEntries = [...currentEntries, ''];
            return {
                ...transaction,
                glovoSummary: {
                    ...transaction.glovoSummary,
                    adBudgetEntries: nextEntries,
                    adBudgetHt: sumGlovoAdBudgetEntries(nextEntries)
                }
            };
        };

        setFacture((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardList((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardTransaction((prev) => patchTransaction(prev));
    };

    const removeGlovoBudgetEntry = (remiseId, index) => {
        const patchTransaction = (transaction) => {
            if (!transaction || transaction.remiseId !== remiseId || !transaction.isGlovo) return transaction;
            const currentEntries = getGlovoAdBudgetEntries(transaction);
            const filteredEntries = currentEntries.filter((_, entryIndex) => entryIndex !== index);
            const nextEntries = filteredEntries.length > 0 ? filteredEntries : [''];
            return {
                ...transaction,
                glovoSummary: {
                    ...transaction.glovoSummary,
                    adBudgetEntries: nextEntries,
                    adBudgetHt: sumGlovoAdBudgetEntries(nextEntries)
                }
            };
        };

        setFacture((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardList((prev) => Array.isArray(prev) ? prev.map(patchTransaction) : prev);
        setWizardTransaction((prev) => patchTransaction(prev));
    };

    const parseExcelDate = (value) => {
        if (value instanceof Date && !isNaN(value)) {
            return value;
        }
        if (typeof value === 'number') {
            const parsed = XLSX.SSF.parse_date_code(value);
            if (parsed?.y && parsed?.m && parsed?.d) {
                return new Date(parsed.y, parsed.m - 1, parsed.d);
            }
        }
        if (typeof value === 'string') {
            const normalized = value.trim();

            const isoMatch = normalized.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
            if (isoMatch) {
                const year = Number(isoMatch[1]);
                const month = Number(isoMatch[2]) - 1;
                const day = Number(isoMatch[3]);
                const isoDate = new Date(year, month, day);
                if (!isNaN(isoDate)) return isoDate;
            }

            const ddMmYyyyMatch = normalized.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
            if (ddMmYyyyMatch) {
                const dd = Number(ddMmYyyyMatch[1]);
                const mm = Number(ddMmYyyyMatch[2]) - 1;
                const yy = Number(ddMmYyyyMatch[3].length === 2 ? `20${ddMmYyyyMatch[3]}` : ddMmYyyyMatch[3]);
                const parsedDdMmYyyy = new Date(yy, mm, dd);
                if (!isNaN(parsedDdMmYyyy)) return parsedDdMmYyyy;
            }

            const dt = new Date(normalized);
            if (!isNaN(dt)) return dt;
        }
        return null;
    };

    const toIsoDateFromInvoiceDate = (value) => {
        const parsedDate = parseExcelDate(value);
        if (!parsedDate) return new Date().toISOString().slice(0, 10);
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatIsoDateForDisplay = (value) => {
        const parsedDate = parseExcelDate(value);
        if (!parsedDate) return value;
        return new Intl.DateTimeFormat('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(parsedDate);
    };

    const closeReglementDateModal = (resolvedValue = null) => {
        const resolver = reglementDateResolverRef.current;
        reglementDateResolverRef.current = null;
        setReglementDateModal({ open: false, invoiceId: null, minDate: '', value: '' });
        if (resolver) {
            resolver(resolvedValue);
        }
    };

    const askReglementDate = (invoiceId) => {
        const currentTransaction = Array.isArray(facture)
            ? facture.find((item) => item?.invoiceId == invoiceId)
            : null;
        const minDate = toIsoDateFromInvoiceDate(currentTransaction?.datef);
        const todayIso = new Date().toISOString().slice(0, 10);
        const defaultDate = todayIso < minDate ? minDate : todayIso;

        setReglementDateModal({
            open: true,
            invoiceId,
            minDate,
            value: defaultDate
        });

        return new Promise((resolve) => {
            reglementDateResolverRef.current = resolve;
        });
    };

    const toUnixTimestampFromIsoDate = (value) => {
        const isoValue = String(value || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(isoValue)) return null;
        const parsedDate = new Date(`${isoValue}T00:00:00`);
        if (Number.isNaN(parsedDate.getTime())) return null;
        return Math.floor(parsedDate.getTime() / 1000);
    };

    const parseCarrefourExcelFile = async (file) => {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const today = new Date();
        const todayDatef = toDdMmYyyy(today);
        const sheetTransactions = [];

        workbook.SheetNames.forEach((sheetName, sheetIndex) => {
            const worksheet = workbook.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            if (!Array.isArray(rows) || rows.length === 0) return;

            const wanted = {
                libelle: ['LIBELLE'],
                caHt: ['CAHT'],
                caTtc: ['CA_TTC', 'CATTC', 'CA TTC'],
                qty: ['QTEVENDUE'],
                tva: ['TAUXTVA'],
                libSite: ['LIBSITE']
            };

            let bestRowIndex = -1;
            let bestCols = null;
            let bestScore = -1;

            rows.slice(0, 150).forEach((row, rowIndex) => {
                const cols = {};
                let score = 0;
                const normalizedRow = (Array.isArray(row) ? row : []).map(cell => normalizeHeaderKey(cell));

                Object.entries(wanted).forEach(([key, aliases]) => {
                    const idx = normalizedRow.findIndex(cell => aliases.some(a => cell === a || cell.includes(a)));
                    if (idx !== -1) {
                        cols[key] = idx;
                        score += 1;
                    }
                });

                if (score > bestScore) {
                    bestScore = score;
                    bestCols = cols;
                    bestRowIndex = rowIndex;
                }
            });

            if (
                bestScore < 3 ||
                bestCols?.libelle === undefined ||
                bestCols?.qty === undefined ||
                (bestCols?.caTtc === undefined && bestCols?.caHt === undefined)
            ) {
                return;
            }

            const lines = [];
            let firstLibSiteValue = '';

            for (let i = bestRowIndex + 1; i < rows.length; i += 1) {
                const row = rows[i] || [];
                const libelle = String(row[bestCols.libelle] || '').trim();
                const caTtcRaw = bestCols.caTtc !== undefined ? parseNumericValue(row[bestCols.caTtc]) : NaN;
                const caHtRaw = bestCols.caHt !== undefined ? parseNumericValue(row[bestCols.caHt]) : NaN;
                const qty = parseNumericValue(row[bestCols.qty]);
                const libSite = bestCols.libSite !== undefined ? String(row[bestCols.libSite] || '').trim() : '';

                if (!firstLibSiteValue && libSite) {
                    firstLibSiteValue = libSite;
                }
                if (!libelle || !Number.isFinite(qty) || qty === 0) {
                    continue;
                }

                const qtyValue = Number(qty);
                if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
                    continue;
                }

                const caHtFromTtc = Number.isFinite(caTtcRaw) ? (caTtcRaw / 1.2) : NaN;
                const effectiveCaHt = Number.isFinite(caHtFromTtc) ? caHtFromTtc : caHtRaw;
                if (!Number.isFinite(effectiveCaHt)) {
                    continue;
                }

                lines.push({
                    multicurrency_code: 'MAD',
                    label: libelle,
                    desc: libelle,
                    tva_tx: DEFAULT_TVA,
                    qty: qtyValue,
                    subprice: (effectiveCaHt / qtyValue),
                    ca_ht: effectiveCaHt,
                    product_type: 'product',
                    trx: qtyValue
                });
            }

            const f61Raw = worksheet['F61']?.v;
            const a3Value = worksheet['A3']?.v;
            const a3Date = parseExcelDate(a3Value);
            const monthYear = a3Date
                ? `${String(a3Date.getMonth() + 1).padStart(2, '0')}/${a3Date.getFullYear()}`
                : '';
            const monthYearCompact = a3Date
                ? `${String(a3Date.getMonth() + 1).padStart(2, '0')}${a3Date.getFullYear()}`
                : '';

            const sumCaHt = lines.reduce((sum, line) => sum + (Number(line?.ca_ht) || 0), 0);

            // Redevance detection rule:
            // find a cell containing "redevance", then read the value from +2 columns on the same row.
            // In Carrefour files this is often the HT amount to deduct (not a rate).
            let redevanceHt = NaN;
            let redevanceRate = NaN;
            for (let i = 0; i < rows.length; i += 1) {
                const row = rows[i] || [];
                for (let j = 0; j < row.length; j += 1) {
                    const cellValue = String(row[j] || '').toLowerCase().trim();
                    if (!cellValue.includes('redevance')) continue;

                    const plusTwoValue = parseNumericValue(row[j + 2]);
                    if (Number.isFinite(plusTwoValue) && plusTwoValue !== 0) {
                        redevanceHt = plusTwoValue;
                    }

                    const percentMatch = String(row[j] || '').match(/(\d+(?:[.,]\d+)?)\s*%/);
                    if (percentMatch) {
                        const percentRaw = parseNumericValue(percentMatch[1]);
                        const parsedRate = normalizeRate(percentRaw);
                        if (Number.isFinite(parsedRate)) {
                            redevanceRate = parsedRate;
                        }
                    }

                    if (!Number.isFinite(redevanceRate)) {
                        const candidateRate = normalizeRate(plusTwoValue);
                        if (Number.isFinite(candidateRate)) {
                            redevanceRate = candidateRate;
                        }
                    }
                    break;
                }
                if (Number.isFinite(redevanceHt) || Number.isFinite(redevanceRate)) break;
            }

            // Fallback from F61 if no explicit redevance amount was found.
            const f61Value = parseNumericValue(f61Raw);
            if (!Number.isFinite(redevanceHt)) {
                if (!Number.isFinite(redevanceRate)) {
                    redevanceRate = normalizeRate(f61Value);
                }
                redevanceHt = Number.isFinite(redevanceRate) ? (sumCaHt * redevanceRate) : 0;
            }

            if (redevanceHt !== 0) {
                lines.push({
                    multicurrency_code: 'MAD',
                    label: `Redevance ${monthYear}`.trim(),
                    desc: `Redevance ${monthYear}`.trim(),
                    tva_tx: DEFAULT_TVA,
                    qty: 1,
                    subprice: -redevanceHt,
                    ca_ht: -redevanceHt,
                    product_type: 'product',
                    trx: 1
                });
            }

            if (!lines.length) return;

            const isHyper = firstLibSiteValue.toLowerCase().includes('hyper');
            const clientId = isHyper ? 507 : 272;

            sheetTransactions.push({
                socid: clientId,
                fk_account: clientId,
                datef: todayDatef,
                remiseId: `CARREFOUR_OFLORIST ${sheetName}_${sheetIndex + 1} ${monthYearCompact}`.trim(),
                note_private: `${firstLibSiteValue || 'Carrefour'} ${monthYearCompact}`.trim(),
                lines,
                company: chosenCompany?.label || chosenCompany?.value || 'oflorist',
                processed: false,
                isCarrefour: true,
                libSite: firstLibSiteValue,
                redevance_rate: redevanceRate,
                sum_caht: sumCaHt
            });
        });

        return sheetTransactions;
    };

    const ProcessedTransactionsCmiView = async (trx) => {
        if (!trx || !Array.isArray(trx) || trx.length === 0) {
            console.warn('ProcessedTransactionsCmiView: Invalid or empty transaction array');
            return;
        }
        
        var allRemiseNumbers = trx.map(m => m.remiseId);
        const transactionTypes = [...new Set(trx.map(getTransactionType))];

        const resPegasus = await axios.post(
            process.env.REACT_APP_PEGASUS_API_URL + "/getProcessedCmi",
            {
                remises: allRemiseNumbers,
                company: chosenCompany.value,
                type: transactionTypes.length === 1 ? transactionTypes[0] : transactionTypes
            },
            header()
        );

        if (resPegasus.status != 200) {
            alert("Erreur attention à vos actions , de préference appeler l'administrateur");
        } else {
            setFacture(trx.map((m) => {
                const matched = resPegasus.data.find(r => r.remiseId == m.remiseId);
                const invoiceId = matched?.invoiceId || matched?.id || m.invoiceId || null;
                const invoiceNumber = matched?.invoiceNumber || matched?.invoice_number || m.invoiceNumber || null;
                const stage = matched?.stage || matched?.status || getVpsStage({ ...m, invoiceId, invoiceNumber });

                return {
                    ...m,
                    processed: Boolean(matched || invoiceId || invoiceNumber),
                    invoiceId,
                    invoiceNumber,
                    stage
                };
            }));
            setSelectedOrders(selectedOrders.filter(x => !resPegasus.data.includes(x.remiseId)));

        }

    }
    const validateAll = async () => {
        if (facture && selectedOrders.length > 0) {
            selectedOrders.map(s => {
                const invoiceid = facture.find(x => x.remiseId == s)?.invoiceId;
                if (invoiceid) {
                    validateInvoice(invoiceid);

                }

            });
            setSelectedOrders([]);
        }


    }

    const buildInvoicePayloadFromTransaction = (transaction) => {
        const isCarrefour = Boolean(
            transaction?.isCarrefour ||
            (typeof transaction?.remiseId === 'string' && transaction.remiseId.startsWith('CARREFOUR_'))
        );
        const isGlovo = Boolean(
            transaction?.isGlovo ||
            (typeof transaction?.remiseId === 'string' && transaction.remiseId.startsWith('GLOVO_'))
        );
        const sourceLines = Array.isArray(transaction?.lines) ? transaction.lines : [];
        const transactionType = isCarrefour
            ? 'carrefour'
            : isGlovo
                ? 'glovo'
                : transaction?.isVps
                    ? 'vps'
                    : 'cmi';
        const vpsLabel = transactionType === 'vps'
            ? (sourceLines[0]?.desc || sourceLines[0]?.label || getRandomVpsProductLabel())
            : (sourceLines[0]?.desc || sourceLines[0]?.label || '');

        let payloadLines = (isCarrefour || isGlovo)
            ? sourceLines.map((line) => ({
                multicurrency_code: "MAD",
                label: line?.label || line?.desc || '',
                desc: line?.desc || line?.label || '',
                tva_tx: DEFAULT_TVA,
                qty: Number.isFinite(Number(line?.qty)) ? Number(line.qty) : 1,
                subprice: Number.isFinite(Number(line?.subprice)) ? Number(line.subprice) : 0,
                ca_ht: Number.isFinite(Number(line?.ca_ht)) ? Number(line.ca_ht) : 0,
                product_type: "product",
                trx: Number.isFinite(Number(line?.trx)) ? Number(line.trx) : 1
            }))
            : (transactionType === 'vps'
                ? sourceLines.map((line) => {
                    const lineLabel = line?.desc || line?.label || getRandomVpsProductLabel();
                    return {
                        multicurrency_code: "MAD",
                        label: lineLabel,
                        desc: lineLabel,
                        tva_tx: DEFAULT_TVA,
                        qty: Number.isFinite(Number(line?.qty)) ? Number(line.qty) : 1,
                        subprice: Number.isFinite(Number(line?.subprice)) ? Number(line.subprice) : 0,
                        product_type: "product",
                        trx: Number.isFinite(Number(line?.trx)) ? Number(line.trx) : 1
                    };
                })
                : [{
                    multicurrency_code: "MAD",
                    label: sourceLines[0]?.desc || sourceLines[0]?.label || '',
                    desc: sourceLines[0]?.desc || sourceLines[0]?.label || '',
                    tva_tx: DEFAULT_TVA,
                    qty: 1,
                    subprice: (Number(sourceLines[0]?.subprice) || 0) / 1.2,
                    product_type: "product",
                    trx: Number.parseInt(String(Number(sourceLines[0]?.trx) || 1), 10)
                }]);

        if (isGlovo) {
            const disputeTtc = Number(transaction?.glovoSummary?.disputeTtc) || 0;
            if (disputeTtc > 0) {
                payloadLines = [
                    ...payloadLines,
                    {
                        multicurrency_code: "MAD",
                        label: "Dispute",
                        desc: "Dispute",
                        tva_tx: DEFAULT_TVA,
                        qty: 1,
                        subprice: -roundCurrency(disputeTtc / 1.2),
                        ca_ht: -roundCurrency(disputeTtc / 1.2),
                        product_type: "product",
                        trx: 1
                    }
                ];
            }
        }

        const datef = isCarrefour ? toDdMmYyyy(new Date()) : transaction.datef;
        const socid = (isCarrefour || isGlovo)
            ? Number(transaction?.socid || transaction?.fk_account || 272)
            : transactionType === 'vps'
                ? VPS_OFLOREST_SOCID
            : transaction.socid;
        const fkAccount = (isCarrefour || isGlovo)
            ? Number(transaction?.fk_account || transaction?.socid || 272)
            : transactionType === 'vps'
                ? VPS_OFLOREST_SOCID
            : getCmiBankAccountId(transaction);

        return {
            socid,
            date: datef.split('/').reverse().join('-'),
            datef: datef.split('/').reverse().join('-'),
            remiseId: transaction.remiseId,
            type: transactionType,
            mode_reglement_code: (isCarrefour || isGlovo) ? "VIR" : "CB",
            mode_reglement_id: (isCarrefour || isGlovo) ? "2" : "6",
            fk_account: fkAccount,
            invoiceNumber: "",
            company: chosenCompany.label,
            note_private: isGlovo ? buildGlovoPrivateNote(transaction) : transaction.note_private,
            rawTransactionIds: transactionType === 'vps' && Array.isArray(transaction.rawTransactions)
                ? transaction.rawTransactions.map((item) => item?.transactionId).filter(Boolean)
                : [],
            lines: payloadLines
        };
    };

    const handleActionButtonClick = async () => {
        if (facture && chosenCompany && selectedOrders.length && window.confirm("Etes-vous sûr de bien vouloir traiter le fichier !")) {
            // Filter out VPS transactions - they use a different processing workflow
            var formatedCmiTrx = facture
                .filter(f => selectedOrders.includes(f.remiseId) && !f.isVps)
                .map(m => buildInvoicePayloadFromTransaction(m));

            if (formatedCmiTrx.length === 0) {
                toast.warning("Aucune transaction CMI sélectionnée. Les transactions VPS utilisent un flux de traitement différent.");
                return;
            }

            const dolibarrToken = isOfloristSelected() ? await getDolibarrToken() : null;
            if (isOfloristSelected() && !dolibarrToken) {
                toast.error("Token Dolibarr manquant. Merci de vous reconnecter.");
                return;
            }
            const tmpRepos = await axios.post(process.env.REACT_APP_PEGASUS_API_URL + "/addInvoices", { trx: formatedCmiTrx, company: chosenCompany.value, token: (isOfloristSelected() ? dolibarrToken : getParams()?.token) }
                ,
                header()
            );
            if (tmpRepos.status == 200) {
                toast.success("PROV a été bien ajoutée à Accounting");
                ProcessedTransactionsCmiView(facture);

                // Supprimer les éléments traités de selectedOrders
                formatedCmiTrx.forEach(trx => {
                    const index = selectedOrders.indexOf(trx.remiseId);
                    if (index > -1) {
                        selectedOrders.splice(index, 1);
                    }
                });

                setSelectedOrders([...selectedOrders]);
            } else {
                alert("Une erreur est survenue");
            }
        }


    };
    const handleUpload = async () => {
        console.log("in")
        if (selectedFile && chosenCompany) {
            try {
                toast.info('Traitement du fichier Excel en cours...', { autoClose: 2000 });
                
                if (selectedSystem === 'cmi') {
                    // Process CMI Excel file locally
                    const processedData = await processCmiExcelFile(selectedFile, chosenCompany.value);
                    
                    // Check which transactions are already processed
                    await ProcessedTransactionsCmiView(processedData);
                    
                    toast.success(`${processedData.length} transaction(s) traitée(s) avec succès`, { autoClose: 3000 });
                } else if (selectedSystem === 'vps') {
                    // Process VPS Excel file
                    const processedData = await processVpsExcelFile(selectedFile);
                    const debitTransactions = processedData.vpsDebitTransactions || [];
                    setVpsSummary(processedData.vpsSummary || {});

                    if (!processedData.length && !debitTransactions.length) {
                        throw new Error("Aucune transaction VPS valide n'a été trouvée. Vérifiez que le fichier est bien au format VPS et que les codes d'action sont à 000.");
                    }
                    
                    // Send VPS data to backend using the utility method
                    const response = await postVpsTransactions([...processedData, ...debitTransactions]);

                    // Display must always be rebuilt from the uploaded file. The backend response can
                    // contain only inserted/duplicate subsets depending on previous imports.
                    const allTransactions = mergeVpsTransactionsWithBackend(processedData, response);
                    const groupedVpsData = groupVpsTransactionsByDate(allTransactions);
                    const allDebitTransactions = mergeVpsTransactionsWithBackend(debitTransactions, response);
                    setVpsDebitGroups(groupVpsDebitTransactionsByTable(allDebitTransactions));
                    setVpsActiveTab(groupedVpsData.length ? 'credits' : 'debits');

                    if (groupedVpsData.length) {
                        await ProcessedTransactionsCmiView(groupedVpsData);
                    } else {
                        setFacture([]);
                    }
                    
                    toast.success(
                        `VPS: ${response.inserted || 0} nouvelles, ${response.duplicates || 0} doublons, ${response.errors || 0} erreurs`,
                        { autoClose: 3000 }
                    );
                } else if (selectedSystem === 'carrefour') {
                    if (!isOfloristSelected()) {
                        toast.error("Carrefour est disponible uniquement pour la société oflorist");
                        return;
                    }

                    const processedData = await parseCarrefourExcelFile(selectedFile);
                    await ProcessedTransactionsCmiView(processedData);
                    toast.success(`${processedData.length} feuille(s) Carrefour traitée(s) avec succès`, { autoClose: 3000 });
                } else if (selectedSystem === 'glovo') {
                    const processedData = await parseGlovoCsvFile(selectedFile);
                    await ProcessedTransactionsCmiView(processedData);
                    toast.success(`${processedData.length} fichier Glovo traité avec succès`, { autoClose: 3000 });
                }
            } catch (error) {
                console.error('Error processing file:', error);
                toast.error('Erreur lors du traitement du fichier: ' + error.message, { autoClose: 5000 });
            }
        } else {
            if (!selectedFile) {
                toast.error('Aucun fichier sélectionné');
            }
            if (!chosenCompany) {
                toast.error('Veuillez sélectionner une entreprise');
            }
        }
    };
    const validateInvoice = async (invoiceId) => {
        if (!invoiceId) {
            toast.error("ID de facture invalide");
            return null;
        }

        const reglementDate = await askReglementDate(invoiceId);
        if (!reglementDate) {
            return null;
        }

        const token = isOfloristSelected() ? await getDolibarrToken() : getParams()?.token;
        if (isOfloristSelected() && !token) {
            alert("Token Dolibarr manquant. Merci de vous reconnecter.");
            return null;
        }

        try {
            console.log('Validating invoice ID:', invoiceId);
            const res = await axios.post(`${getParams()?.urlErp}api/index.php/invoices/${invoiceId}/validate`, {}, {
                ...header({ DOLAPIKEY: token }),
            });

            if (res.status === 200) {
                toast.success("Valider et réference définie " + res.data.ref);
                await updateCmiRef(res.data.id, res.data.ref, reglementDate);
                return res.data.ref; // Return the invoice number
            }
        } catch (error) {
            alert("Une erreur est survenue");
            console.error(error);
        }
        return null;
    }


    const updateCmiRef = async (invoiceId, ref, reglementDate) => {
        try {
            const response = await axios.post(process.env.REACT_APP_PEGASUS_API_URL + "/invoiceValidation", {
                invoiceId,
                ref
            },
                header());
            if (response.status == 200 && response.data.success) {
                const token = isOfloristSelected() ? await getDolibarrToken() : getParams()?.token;
                if (isOfloristSelected() && !token) {
                    toast.error("Token Dolibarr manquant. Merci de vous reconnecter.");
                    return false;
                }
                const currentTransaction = Array.isArray(facture)
                    ? facture.find((item) => item?.invoiceId == invoiceId)
                    : null;
                const isGlovoPayment = Boolean(currentTransaction?.isGlovo);
                const res = isGlovoPayment
                    ? await axios.post(`${getParams()?.urlErp}api/index.php/invoices/${invoiceId}/payments`, {
                        datepaye: toUnixTimestampFromIsoDate(reglementDate),
                        paymentid: 2,
                        closepaidinvoices: 'yes',
                        accountid: getGlovoPaymentAccountId(currentTransaction?.glovoSummary?.storeAddressId),
                        comment: String(currentTransaction?.glovoSummary?.invoiceFileNumber || '')
                    }, {
                        ...header({ DOLAPIKEY: token }),
                    })
                    : await axios.post(`${getParams()?.urlErp}api/index.php/invoices/${invoiceId}/settopaid`, {
                        datepaid: reglementDate
                    }, {
                        ...header({ DOLAPIKEY: token }),
                    });
                if (res.status === 200) {
                    console.log(invoiceId);
                    const updatedFacture = facture.map(f => f.invoiceId == invoiceId ? { ...f, invoiceNumber: ref } : f);
                    setFacture(updatedFacture);
                    toast.success("Payment de la facture " + ref);
                    return true;
                }
            }
        } catch (error) {
            toast.error("Erreur lors du paiement");
        }
        return false;
    }

    const resetComponents = () => {
        setKey(prevKey => prevKey + 1); // Increment key to force remount
        setSelectedFile(null);    // Reset selectedFile to null
        setFacture(null);         // Reset facture to null
        setVpsSummary({});
        setVpsDebitGroups([]);
        setVpsActiveTab('credits');
        setSelectAll(false);      // Reset selectAll to false
        setSelectedOrders([]);    // Reset selectedOrders to an empty array
        setChosenCompany(undefined); // Reset chosenCompany to undefined
        setSelectedSystem('cmi'); // Reset to CMI by default
    };

    const resetForNextVpsFile = () => {
        setSelectedFile(null);
        setFacture(null);
        setVpsSummary({});
        setVpsDebitGroups([]);
        setVpsActiveTab('credits');
        setSelectAll(false);
        setSelectedOrders([]);
        setExpandedRows([]);
        setWizardTransaction(null);
        setWizardStep('confirm');
        setWizardProcessing(false);
        setProcessedInvoiceId(null);
        setWizardList([]);
        setWizardCurrentIndex(0);
        setLastProcessedInWizard(null);
        setWizardShowDetails(false);
        setFileInputKey((prevKey) => prevKey + 1);
        setSelectedSystem('vps');
    };


    const handleSelectAllChange = () => {
        setSelectAll(!selectAll);
        if (!selectAll) {
            const allOrderNotes = facture.filter(x => !isVpsEffectivelyProcessed(x)).map((f) => f.remiseId);
            setSelectedOrders(allOrderNotes);
        } else {
            setSelectedOrders([]);
        }
    };

    // Function to merge selected VPS groups
    const mergeSelectedVps = () => {
        if (!facture || selectedOrders.length < 2) {
            toast.warning("Veuillez sélectionner au moins 2 groupes VPS à fusionner");
            return;
        }

        // Get selected VPS groups only
        const selectedVpsGroups = facture.filter(f => 
            selectedOrders.includes(f.remiseId) && f.isVps && !isVpsEffectivelyProcessed(f)
        );

        if (selectedVpsGroups.length < 2) {
            toast.warning("Veuillez sélectionner au moins 2 groupes VPS non traités");
            return;
        }

        // Combine all transactions from selected groups
        const allRawTransactions = [];
        const mergedLines = [];
        const dates = [];

        selectedVpsGroups.forEach(group => {
            allRawTransactions.push(...group.rawTransactions);
            mergedLines.push(...(Array.isArray(group.lines) ? group.lines : []));
            dates.push(group.datef);
        });

        // Sort dates to get range
        const sortedDates = dates.sort((a, b) => {
            const [dayA, monthA, yearA] = a.split('/');
            const [dayB, monthB, yearB] = b.split('/');
            return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
        });

        const dateRange = sortedDates.length > 1 
            ? `${sortedDates[0]} - ${sortedDates[sortedDates.length - 1]}`
            : sortedDates[0];

        // Create merged group
        const mergedGroup = {
            socid: VPS_OFLOREST_SOCID,
            remiseId: `VPS_MERGED_${Date.now()}`,
            company: 'vps',
            datef: dateRange,
            note_private: `VPS Fusionné - ${dateRange} (${selectedVpsGroups.length} groupes)`,
            rawTransactions: allRawTransactions,
            lines: mergedLines,
            processed: false,
            invoiceId: null,
            invoiceNumber: null,
            isVps: true,
            isMerged: true // Flag to identify merged groups
        };

        // Remove selected groups and add merged group
        const updatedFacture = [
            ...facture.filter(f => !selectedOrders.includes(f.remiseId)),
            mergedGroup
        ];

        setFacture(updatedFacture);
        setSelectedOrders([]);
        toast.success(`${selectedVpsGroups.length} groupes VPS fusionnés avec succès`);
    };

    // Wizard functions
    const startWizard = (transaction) => {
        setWizardList([transaction]);
        setWizardCurrentIndex(0);
        setWizardTransaction(transaction);
        setWizardStep('confirm');
        setWizardProcessing(false);
        setProcessedInvoiceId(null);
        setLastProcessedInWizard(null); // Reset when starting new wizard
        setWizardShowDetails(false); // Reset details visibility
    };

    const startWizardForAll = () => {
        // Show ALL transactions (processed and unprocessed)
        if (facture.length === 0) {
            toast.info("Aucune transaction disponible");
            return;
        }
        setWizardList(facture);
        setWizardCurrentIndex(0);
        setWizardTransaction(facture[0]);
        setWizardStep('confirm');
        setWizardProcessing(false);
        setProcessedInvoiceId(null);
        setLastProcessedInWizard(null); // Reset when starting new wizard
        setWizardShowDetails(false); // Reset details visibility
    };

    const closeWizard = () => {
        setWizardTransaction(null);
        setWizardList([]);
        setWizardCurrentIndex(0);
        setWizardStep('confirm');
        setWizardProcessing(false);
        setProcessedInvoiceId(null);
        setLastProcessedInWizard(null); // Reset last processed when closing wizard
        setWizardShowDetails(false); // Reset details visibility
    };

    const goToPreviousTransaction = () => {
        if (wizardCurrentIndex > 0) {
            const newIndex = wizardCurrentIndex - 1;
            setWizardCurrentIndex(newIndex);
            setWizardTransaction(wizardList[newIndex]);
            setWizardStep('confirm');
            setProcessedInvoiceId(null);
            setWizardShowDetails(false); // Reset details visibility when navigating
            // Keep lastProcessedInWizard when going back to review
        }
    };

    const goToNextTransaction = () => {
        if (wizardCurrentIndex < wizardList.length - 1) {
            const newIndex = wizardCurrentIndex + 1;
            setWizardCurrentIndex(newIndex);
            setWizardTransaction(wizardList[newIndex]);
            setWizardStep('confirm');
            setProcessedInvoiceId(null);
            setWizardShowDetails(false); // Reset details visibility when navigating
            // Keep lastProcessedInWizard - don't reset it when navigating
        } else {
            // No more transactions, close wizard
            toast.success("Toutes les transactions ont été traitées!");
            closeWizard();
        }
    };

    const processSingleTransaction = async () => {
        if (!wizardTransaction) return;

        setWizardProcessing(true);

        try {
            const formatedCmiTrx = [buildInvoicePayloadFromTransaction(wizardTransaction)];

            const dolibarrToken = isOfloristSelected() ? await getDolibarrToken() : null;
            if (isOfloristSelected() && !dolibarrToken) {
                toast.error("Token Dolibarr manquant. Merci de vous reconnecter.");
                setWizardProcessing(false);
                return;
            }

            const tmpRepos = await axios.post(
                process.env.REACT_APP_PEGASUS_API_URL + "/addInvoices",
                { trx: formatedCmiTrx, company: chosenCompany.value, token: (isOfloristSelected() ? dolibarrToken : getParams()?.token) },
                header()
            );

            if (tmpRepos.status == 200) {
                toast.success("Transaction ajoutée à Accounting avec succès!");
                
                // Get invoice ID from response - API returns: {data: "hello", results: [{trx: "000365", id: 5429}]}
                const invoiceId = tmpRepos.data?.results?.[0]?.id || 
                                  tmpRepos.data?.invoices?.[0]?.invoiceId || 
                                  tmpRepos.data?.[0]?.invoiceId || 
                                  null;
                
                console.log('Invoice ID extracted:', invoiceId, 'from response:', tmpRepos.data);
                setProcessedInvoiceId(invoiceId);
                
                // Save last processed transaction info
                setLastProcessedInWizard({
                    remiseId: wizardTransaction.remiseId,
                    note_private: wizardTransaction.note_private,
                    invoiceId: invoiceId,
                    amount: getTransactionTotal(wizardTransaction),
                    currency: wizardTransaction.lines[0].currency
                });
                
                // Update the facture array with the processed transaction
                const updatedFacture = facture.map(f => 
                    f.remiseId === wizardTransaction.remiseId 
                        ? { ...f, processed: true, invoiceId: invoiceId, stage: 'créée' }
                        : f
                );
                setFacture(updatedFacture);
                
                // Update the wizard list to reflect processed status
                const updatedWizardList = wizardList.map(f =>
                    f.remiseId === wizardTransaction.remiseId
                        ? { ...f, processed: true, invoiceId: invoiceId, stage: 'créée' }
                        : f
                );
                setWizardList(updatedWizardList);
                
                // Update the current wizard transaction to show new status
                setWizardTransaction({
                    ...wizardTransaction,
                    processed: true,
                    invoiceId: invoiceId,
                    stage: 'créée'
                });
                
                // Remove from selected orders
                setSelectedOrders(selectedOrders.filter(id => id !== wizardTransaction.remiseId));
                
                setWizardProcessing(false);
                // Stay on confirm step to show updated status instead of going to invoice step
                setWizardStep('confirm');
            } else {
                toast.error("Une erreur est survenue lors du traitement");
                setWizardProcessing(false);
            }
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors du traitement de la transaction");
            setWizardProcessing(false);
        }
    };

    const handleWizardValidateInvoice = async () => {
        // Use processedInvoiceId if available, otherwise use wizardTransaction.invoiceId
        const invoiceIdToValidate = processedInvoiceId || wizardTransaction?.invoiceId;
        
        if (!invoiceIdToValidate) {
            toast.error("Aucune facture à valider");
            return;
        }

        console.log('Validating invoice in wizard. Invoice ID:', invoiceIdToValidate);
        setWizardProcessing(true);
        try {
            // Get the invoice number directly from validateInvoice
            const invoiceNumber = await validateInvoice(invoiceIdToValidate);
            
            console.log('Received invoice number from validation:', invoiceNumber);
            
            if (invoiceNumber) {
                // Update wizard transaction with invoice number immediately
                setWizardTransaction(prevTransaction => {
                    console.log('Updating wizard transaction. Previous:', prevTransaction, 'New invoice number:', invoiceNumber);
                    return {
                        ...prevTransaction,
                        invoiceNumber: invoiceNumber,
                        stage: 'validée'
                    };
                });
                
                // Update wizard list
                setWizardList(prevList => prevList.map(f =>
                    f.invoiceId === invoiceIdToValidate
                        ? { ...f, invoiceNumber: invoiceNumber, stage: 'validée' }
                        : f
                ));
                
                console.log('Invoice validated and wizard updated with number:', invoiceNumber);
            } else {
                console.warn('No invoice number returned from validation');
            }
            
            setWizardProcessing(false);
            // Stay on current transaction to show validated status - let user click next manually
        } catch (error) {
            console.error('Error validating invoice:', error);
            setWizardProcessing(false);
        }
    };

    const hasMultiple = wizardList.length > 1;
    const hasPrevious = wizardCurrentIndex > 0;
    const hasNext = wizardCurrentIndex < wizardList.length - 1;
    const previousTransaction = hasPrevious ? wizardList[wizardCurrentIndex - 1] : null;
    const nextTransaction = hasNext ? wizardList[wizardCurrentIndex + 1] : null;
    const currentFactureIndex = facture?.findIndex(f => f.remiseId === wizardTransaction?.remiseId) ?? -1;
    const previousInFacture = currentFactureIndex > 0 ? facture[currentFactureIndex - 1] : null;
    const nextInFacture = currentFactureIndex >= 0 && currentFactureIndex < (facture?.length ?? 0) - 1 ? facture[currentFactureIndex + 1] : null;

    useEffect(() => {
        if (!wizardTransaction || wizardProcessing || wizardStep !== 'confirm') return undefined;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft' && hasPrevious) {
                e.preventDefault();
                goToPreviousTransaction();
            } else if (e.key === 'ArrowRight' && hasNext) {
                e.preventDefault();
                goToNextTransaction();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasNext, hasPrevious, wizardCurrentIndex, wizardList.length, wizardProcessing, wizardStep, wizardTransaction]);

    const renderWizardModal = () => {
        if (!wizardTransaction) return null;

        return (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header bg-primary text-white">
                            <h5 className="modal-title">
                                <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                                Assistant de Traitement
                                {hasMultiple && (
                                    <span className="badge bg-light text-primary ms-2">
                                        {wizardCurrentIndex + 1} / {wizardList.length}
                                    </span>
                                )}
                            </h5>
                            <button type="button" className="btn-close btn-close-white" onClick={closeWizard} disabled={wizardProcessing}></button>
                        </div>
                        <div className="modal-body p-4">
                            {/* Show last processed transaction in this wizard session */}
                            {lastProcessedInWizard && (
                                <div className="alert alert-success mb-3">
                                    <div className="d-flex align-items-center">
                                        <i className="fa-solid fa-check-circle fa-2x me-3 text-success"></i>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-1">
                                                <i className="fa-solid fa-history me-2"></i>
                                                Dernière Transaction Traitée
                                            </h6>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <small>
                                                        <strong>Remise:</strong> {lastProcessedInWizard.note_private}
                                                    </small>
                                                </div>
                                                <div className="col-md-6">
                                                    <small>
                                                        <strong>Facture ID:</strong> {lastProcessedInWizard.invoiceId}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12">
                                                    <small className="text-muted">
                                                        <strong>Montant:</strong> {lastProcessedInWizard.amount} {lastProcessedInWizard.currency}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Navigation info - show previous/next transaction details from full facture list */}
                            {hasMultiple && (
                                <div className="row mb-3">
                                    {previousInFacture && (
                                        <div className="col-md-6">
                                            <div className={`alert ${previousInFacture.processed && previousInFacture.invoiceNumber ? 'alert-success' : 'alert-info'} py-2 mb-0`}>
                                                <small>
                                                    <i className="fa-solid fa-arrow-left me-1"></i>
                                                    <strong>Précédent:</strong> {previousInFacture.note_private}
                                                    {previousInFacture.invoiceNumber && (
                                                        <span className="d-block ms-3">
                                                            <i className="fa-solid fa-file-invoice me-1"></i>
                                                            <strong>Facture: {previousInFacture.invoiceNumber}</strong>
                                                        </span>
                                                    )}
                                                    {previousInFacture.processed && previousInFacture.invoiceId && !previousInFacture.invoiceNumber && (
                                                        <span className="d-block ms-3 text-muted">
                                                            <i className="fa-solid fa-check-circle me-1"></i>
                                                            Traité - ID: {previousInFacture.invoiceId} (en attente de validation)
                                                        </span>
                                                    )}
                                                    {!previousInFacture.processed && (
                                                        <span className="d-block ms-3 text-warning">
                                                            <i className="fa-solid fa-hourglass me-1"></i>
                                                            Non traité
                                                        </span>
                                                    )}
                                                </small>
                                            </div>
                                        </div>
                                    )}
                                    {nextInFacture && (
                                        <div className="col-md-6">
                                            <div className={`alert ${nextInFacture.processed && nextInFacture.invoiceNumber ? 'alert-success' : 'alert-secondary'} py-2 mb-0`}>
                                                <small>
                                                    <i className="fa-solid fa-arrow-right me-1"></i>
                                                    <strong>Suivant:</strong> {nextInFacture.note_private}
                                                    {nextInFacture.invoiceNumber && (
                                                        <span className="d-block ms-3">
                                                            <i className="fa-solid fa-file-invoice me-1"></i>
                                                            <strong>Facture: {nextInFacture.invoiceNumber}</strong>
                                                        </span>
                                                    )}
                                                    {nextInFacture.processed && nextInFacture.invoiceId && !nextInFacture.invoiceNumber && (
                                                        <span className="d-block ms-3 text-muted">
                                                            <i className="fa-solid fa-check-circle me-1"></i>
                                                            Traité - ID: {nextInFacture.invoiceId} (en attente de validation)
                                                        </span>
                                                    )}
                                                    {!nextInFacture.processed && (
                                                        <span className="d-block ms-3 text-warning">
                                                            <i className="fa-solid fa-hourglass me-1"></i>
                                                            Non traité
                                                        </span>
                                                    )}
                                                </small>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {wizardStep === 'confirm' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Détails de la transaction</h5>
                                        {wizardTransaction.processed && wizardTransaction.invoiceNumber && (
                                            <span className="badge bg-success fs-6">
                                                <i className="fa-solid fa-check-circle me-1"></i>
                                                Déjà Validée
                                            </span>
                                        )}
                                        {wizardTransaction.processed && wizardTransaction.invoiceId && !wizardTransaction.invoiceNumber && (
                                            <span className="badge bg-warning fs-6">
                                                <i className="fa-solid fa-hourglass me-1"></i>
                                                En attente de validation
                                            </span>
                                        )}
                                        {!wizardTransaction.processed && (
                                            <span className="badge bg-info fs-6">
                                                <i className="fa-solid fa-clock me-1"></i>
                                                Non traitée
                                            </span>
                                        )}
                                    </div>
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row mb-2">
                                                <div className="col-md-6">
                                                    <strong>Remise Numéro:</strong>
                                                    <p className="text-primary fs-5">{wizardTransaction.note_private}</p>
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Nombre de transactions:</strong>
                                                    <p className="text-info fs-5">
                                                        {(wizardTransaction.isCarrefour || wizardTransaction.isGlovo)
                                                            ? wizardTransaction.lines.reduce((acc, line) => acc + (Number(line?.trx) || 0), 0)
                                                            : wizardTransaction.lines[0].trx}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="row mb-2">
                                                <div className="col-md-6">
                                                    <strong>Type:</strong>
                                                    <p>
                                                        {wizardTransaction.isCarrefour ? (
                                                            <span className="badge bg-info">CARREFOUR</span>
                                                        ) : wizardTransaction.isGlovo ? (
                                                            <span className="badge bg-dark">GLOVO</span>
                                                        ) : (
                                                            <span className={`badge bg-${getParams()?.site == wizardTransaction.socid ? "primary" : "warning"}`}>
                                                                {getParams()?.site == wizardTransaction.socid ? "SITE" : "TPE"}
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Date:</strong>
                                                    <p>{wizardTransaction.datef}</p>
                                                </div>
                                            </div>
                                            <div className="row mb-2">
                                                <div className="col-12">
                                                    <strong>Produit:</strong>
                                                    <p>{wizardTransaction.isGlovo ? `Lignes: ${wizardTransaction.lines.length}` : wizardTransaction.lines[0].desc}</p>
                                                </div>
                                            </div>
                                            {wizardTransaction.isGlovo && (() => {
                                                const glovoTotals = calculateGlovoFinancials(wizardTransaction);
                                                const glovoBWithDispute = roundCurrency(glovoTotals.bTtc - glovoTotals.disputeTtc);
                                                const adBudgetEntries = getGlovoAdBudgetEntries(wizardTransaction);
                                                return (
                                                    <div className="row mb-3 g-3 align-items-end">
                                                        <div className="col-md-6">
                                                            <label className="form-label d-flex justify-content-between align-items-center">
                                                                <span>Ad budget HT</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => addGlovoBudgetEntry(wizardTransaction.remiseId)}
                                                                    disabled={wizardProcessing}
                                                                >
                                                                    <i className="fa-solid fa-plus me-1"></i>
                                                                    Ajouter
                                                                </button>
                                                            </label>
                                                            <div className="d-flex flex-column gap-2">
                                                                {adBudgetEntries.map((entry, entryIndex) => (
                                                                    <div className="input-group" key={`wizard-ad-budget-${entryIndex}`}>
                                                                        <input
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            className="form-control"
                                                                            value={entry}
                                                                            onChange={(e) => updateGlovoBudgetEntry(wizardTransaction.remiseId, entryIndex, e.target.value)}
                                                                            disabled={wizardProcessing}
                                                                        />
                                                                        {adBudgetEntries.length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-danger"
                                                                                onClick={() => removeGlovoBudgetEntry(wizardTransaction.remiseId, entryIndex)}
                                                                                disabled={wizardProcessing}
                                                                            >
                                                                                <i className="fa-solid fa-trash"></i>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <label className="form-label">Dispute TTC</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                className="form-control"
                                                                value={Number(wizardTransaction?.glovoSummary?.disputeTtc || 0)}
                                                                onChange={(e) => updateGlovoTransaction(wizardTransaction.remiseId, 'disputeTtc', e.target.value)}
                                                                disabled={wizardProcessing}
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="small text-muted">
                                                                <div>Base commission TTC: <strong>{glovoTotals.baseCommissionTtc.toFixed(2)} MAD</strong></div>
                                                                <div>Commission facturée HT: <strong>{glovoTotals.commissionBilledHt.toFixed(2)} MAD</strong></div>
                                                                <div>A TTC: <strong>{glovoTotals.aTtc.toFixed(2)} MAD</strong></div>
                                                                <div>B TTC: <strong>{glovoBWithDispute.toFixed(2)} MAD</strong></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {wizardTransaction.rawTransactions && wizardTransaction.rawTransactions.length > 0 && (
                                                <div className="row mb-3">
                                                    <div className="col-md-4">
                                                        <strong>Montant Brut TTC:</strong>
                                                        <h5 className="text-primary mb-0">
                                                            {wizardTransaction.rawTransactions.reduce((sum, trx) => sum + parseFloat(trx.montantBrutTTC || 0), 0).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                        </h5>
                                                        <small className="text-muted">Montant brut</small>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <strong>Total Commissions:</strong>
                                                        <h5 className="text-warning mb-0">
                                                            {(wizardTransaction.rawTransactions.reduce((sum, trx) => sum + parseFloat(trx.commission || 0), 0) +
                                                              wizardTransaction.rawTransactions.reduce((sum, trx) => sum + parseFloat(trx.tvaCommission || 0), 0)).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                        </h5>
                                                        <small className="text-muted">Commission + TVA</small>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <strong>Montant Net TTC:</strong>
                                                        <h5 className="text-success mb-0">
                                                            {wizardTransaction.rawTransactions.reduce((sum, trx) => sum + parseFloat(trx.montantNetTTC || 0), 0).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                        </h5>
                                                        <small className="text-muted">Montant net</small>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <strong>Montant Total Comptabilisé:</strong>
                                                    <h3 className="text-success">
                                                        {(wizardTransaction.isCarrefour
                                                            ? (() => {
                                                                const allLines = Array.isArray(wizardTransaction.lines) ? wizardTransaction.lines : [];
                                                                const salesLines = allLines.filter((line) => !String(line?.label || line?.desc || '').toLowerCase().includes('redevance'));
                                                                const redevanceLine = allLines.find((line) => String(line?.label || line?.desc || '').toLowerCase().includes('redevance'));
                                                                const salesCaHt = salesLines.reduce((sum, line) => sum + (Number(line?.ca_ht) || 0), 0);
                                                                const redevanceRawHt = Number(redevanceLine?.ca_ht) || 0;
                                                                const redevanceDeductionHt = redevanceRawHt > 0 ? -redevanceRawHt : redevanceRawHt;
                                                                return salesCaHt + redevanceDeductionHt;
                                                            })()
                                                            : wizardTransaction.lines.reduce((acc, line) => acc + ((Number(line?.subprice) || 0) * (Number(line?.qty) || 1)), 0)
                                                        ).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                    </h3>
                                                    {wizardTransaction.isCarrefour && (
                                                        <small className="text-muted">Inclut la déduction de la redevance</small>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Montant Total Comptabilisé TTC:</strong>
                                                    <h3 className="text-primary">
                                                        {((wizardTransaction.isCarrefour
                                                            ? (() => {
                                                                const allLines = Array.isArray(wizardTransaction.lines) ? wizardTransaction.lines : [];
                                                                const salesLines = allLines.filter((line) => !String(line?.label || line?.desc || '').toLowerCase().includes('redevance'));
                                                                const redevanceLine = allLines.find((line) => String(line?.label || line?.desc || '').toLowerCase().includes('redevance'));
                                                                const salesCaHt = salesLines.reduce((sum, line) => sum + (Number(line?.ca_ht) || 0), 0);
                                                                const redevanceRawHt = Number(redevanceLine?.ca_ht) || 0;
                                                                const redevanceDeductionHt = redevanceRawHt > 0 ? -redevanceRawHt : redevanceRawHt;
                                                                return salesCaHt + redevanceDeductionHt;
                                                            })()
                                                            : wizardTransaction.isGlovo
                                                                ? (
                                                                    wizardTransaction.lines.reduce((acc, line) => acc + ((Number(line?.subprice) || 0) * (Number(line?.qty) || 1)), 0) * 1.2
                                                                ) - (Number(wizardTransaction?.glovoSummary?.disputeTtc) || 0)
                                                            : wizardTransaction.lines.reduce((acc, line) => acc + ((Number(line?.subprice) || 0) * (Number(line?.qty) || 1)), 0)
                                                        ) * (wizardTransaction.isGlovo ? 1 : 1.2)).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                    </h3>
                                                </div>
                                            </div>
                                            {wizardTransaction.isGlovo && (() => {
                                                const glovoTotals = calculateGlovoFinancials(wizardTransaction);
                                                const glovoBWithDispute = roundCurrency(glovoTotals.bTtc - glovoTotals.disputeTtc);
                                                return (
                                                    <div className="row mt-2">
                                                        <div className="col-md-3">
                                                            <strong>Base TTC:</strong>
                                                            <div>{glovoTotals.baseCommissionTtc.toFixed(2)} MAD</div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <strong>Commission HT:</strong>
                                                            <div>{glovoTotals.commissionBilledHt.toFixed(2)} MAD</div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <strong>A TTC:</strong>
                                                            <div>{glovoTotals.aTtc.toFixed(2)} MAD</div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <strong>B TTC:</strong>
                                                            <div className="text-success fw-bold">{glovoBWithDispute.toFixed(2)} MAD</div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {wizardTransaction.isCarrefour && (() => {
                                                const allLines = Array.isArray(wizardTransaction.lines) ? wizardTransaction.lines : [];
                                                const salesLines = allLines.filter((line) => !String(line?.label || line?.desc || '').toLowerCase().includes('redevance'));
                                                const redevanceLine = allLines.find((line) => String(line?.label || line?.desc || '').toLowerCase().includes('redevance'));
                                                const sumCaHt = salesLines.reduce((sum, line) => sum + (Number(line?.ca_ht) || 0), 0);
                                                const redevanceRawHt = Number(redevanceLine?.ca_ht) || 0;
                                                const redevanceDeductionHt = redevanceRawHt > 0 ? -redevanceRawHt : redevanceRawHt;
                                                const bonAPayerHt = sumCaHt + redevanceDeductionHt;

                                                return (
                                                    <div className="row mt-2">
                                                        <div className="col-md-4">
                                                            <strong>SUM(CA_HT):</strong>
                                                            <div>{sumCaHt.toFixed(2)} {wizardTransaction.lines[0].currency}</div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <strong>Redevance HT (déduite):</strong>
                                                            <div>{redevanceDeductionHt.toFixed(2)} {wizardTransaction.lines[0].currency}</div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <strong>Bon à payer HT:</strong>
                                                            <div className="text-success fw-bold">{bonAPayerHt.toFixed(2)} {wizardTransaction.lines[0].currency}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {wizardTransaction.isCarrefour && Array.isArray(wizardTransaction.lines) && wizardTransaction.lines.length > 0 && (
                                                <div className="mt-3">
                                                    <strong>Preview des lignes de facture:</strong>
                                                    <div className="table-responsive mt-2">
                                                        <table className="table table-sm table-bordered">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Libellé</th>
                                                                    <th>TVA %</th>
                                                                    <th>Qté</th>
                                                                    <th>PU (HT)</th>
                                                                    <th>Total Ligne</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {wizardTransaction.lines.map((line, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>{line?.desc || line?.label}</td>
                                                                        <td>{DEFAULT_TVA.toFixed(2)}</td>
                                                                        <td>{Number(line?.qty || 1)}</td>
                                                                        <td>{(line?.ca_ht && Number(line?.qty || 0) > 0 ? (Number(line.ca_ht) / Number(line.qty)) : Number(line?.subprice || 0)).toFixed(2)}</td>
                                                                        <td>{(Number(line?.ca_ht) || 0).toFixed(2)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                            {wizardTransaction.isGlovo && Array.isArray(wizardTransaction.lines) && wizardTransaction.lines.length > 0 && (
                                                <div className="mt-3">
                                                    <strong>Preview des lignes de facture:</strong>
                                                    <div className="table-responsive mt-2">
                                                        <table className="table table-sm table-bordered">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Libellé</th>
                                                                    <th>TVA %</th>
                                                                    <th>Qté</th>
                                                                    <th>PU (HT)</th>
                                                                    <th>Total Ligne</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {wizardTransaction.lines.map((line, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>{line?.desc || line?.label}</td>
                                                                        <td>{DEFAULT_TVA.toFixed(2)}</td>
                                                                        <td>{Number(line?.qty || 1)}</td>
                                                                        <td>{Number(line?.subprice || 0).toFixed(2)}</td>
                                                                        <td>{(Number(line?.subprice || 0) * Number(line?.qty || 1)).toFixed(2)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Collapsible raw transactions details */}
                                    {wizardTransaction.rawTransactions && wizardTransaction.rawTransactions.length > 0 && (
                                        <div className="mb-3">
                                            <button 
                                                className="btn btn-outline-primary btn-sm w-100" 
                                                onClick={() => setWizardShowDetails(!wizardShowDetails)}
                                                type="button"
                                            >
                                                <i className={`fa-solid fa-chevron-${wizardShowDetails ? 'up' : 'down'} me-2`}></i>
                                                {wizardShowDetails ? 'Masquer' : 'Afficher'} les détails des transactions ({wizardTransaction.rawTransactions.length})
                                            </button>
                                            
                                            {wizardShowDetails && (
                                                <div className="bg-light p-3 border-start border-4 border-info mt-2">
                                                    <h6 className="mb-3">
                                                        <i className="fa-solid fa-list me-2"></i>
                                                        Détails des Transactions ({wizardTransaction.rawTransactions.length})
                                                    </h6>
                                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                        <table className="table table-sm table-hover">
                                                            <thead className="table-secondary sticky-top">
                                                                <tr>
                                                                    <th>Date Transaction</th>
                                                                    <th>Heure</th>
                                                                    <th>Système</th>
                                                                    <th>Carte</th>
                                                                    <th>N° Autorisation</th>
                                                                    <th>Montant Brut TTC</th>
                                                                    <th>Commission</th>
                                                                    <th>TVA Comm.</th>
                                                                    <th>Montant Net TTC</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {wizardTransaction.rawTransactions.map((trx, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>{trx.dateTransaction}</td>
                                                                        <td><small>{trx.heure}</small></td>
                                                                        <td>
                                                                            <span className="badge bg-secondary">{trx.systeme}</span>
                                                                        </td>
                                                                        <td><code className="small">{trx.numeroCarte}</code></td>
                                                                        <td><small>{trx.numeroAutorisation}</small></td>
                                                                        <td>
                                                                            <span className="badge bg-primary">
                                                                                {parseFloat(trx.montantBrutTTC).toFixed(2)} {trx.devise}
                                                                            </span>
                                                                        </td>
                                                                        <td><small>{parseFloat(trx.commission || 0).toFixed(2)}</small></td>
                                                                        <td><small>{parseFloat(trx.tvaCommission || 0).toFixed(2)}</small></td>
                                                                        <td>
                                                                            <strong className="text-success">
                                                                                {parseFloat(trx.montantNetTTC).toFixed(2)} {trx.devise}
                                                                            </strong>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className="table-light sticky-bottom">
                                                                <tr>
                                                                    <td colSpan="5" className="text-end fw-bold">Totaux:</td>
                                                                    <td>
                                                                        <span className="badge bg-primary">
                                                                            {wizardTransaction.rawTransactions.reduce((sum, t) => sum + parseFloat(t.montantBrutTTC), 0).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {wizardTransaction.rawTransactions.reduce((sum, t) => sum + parseFloat(t.commission || 0), 0).toFixed(2)}
                                                                    </td>
                                                                    <td>
                                                                        {wizardTransaction.rawTransactions.reduce((sum, t) => sum + parseFloat(t.tvaCommission || 0), 0).toFixed(2)}
                                                                    </td>
                                                                    <td>
                                                                        <strong className="text-success">
                                                                            {wizardTransaction.rawTransactions.reduce((sum, t) => sum + parseFloat(t.montantNetTTC), 0).toFixed(2)} {wizardTransaction.lines[0].currency}
                                                                        </strong>
                                                                    </td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {wizardTransaction.processed && wizardTransaction.invoiceId && (
                                        <div className={`alert ${wizardTransaction.invoiceNumber ? 'alert-success' : 'alert-info'}`}>
                                            <div className="row align-items-center">
                                                <div className="col-md-8">
                                                    <div className="mb-2">
                                                        <i className="fa-solid fa-file-invoice me-2"></i>
                                                        <strong>Facture ID:</strong> 
                                                        <span className="ms-2 fs-5 text-primary">{wizardTransaction.invoiceId}</span>
                                                    </div>
                                                    {wizardTransaction.invoiceNumber && (
                                                        <div>
                                                            <i className="fa-solid fa-receipt me-2"></i>
                                                            <strong>Numéro de Facture:</strong>
                                                            <span className="ms-2 fs-5 text-success fw-bold">{wizardTransaction.invoiceNumber}</span>
                                                            <i className="fa-solid fa-check-circle ms-2 text-success"></i>
                                                        </div>
                                                    )}
                                                    {!wizardTransaction.invoiceNumber && (
                                                        <div className="text-muted">
                                                            <i className="fa-solid fa-hourglass me-2"></i>
                                                            <small>En attente de validation</small>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-md-4 text-end">
                                                    <a 
                                                        href={`${getParams()?.urlErp}compta/facture/card.php?facid=${wizardTransaction.invoiceId}`} 
                                                        target='_blank' 
                                                        rel="noopener noreferrer"
                                                        className='btn btn-info'
                                                    >
                                                        <i className="fa-solid fa-external-link-alt me-2"></i>
                                                        Voir la Facture
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!wizardTransaction.processed && (
                                        <div className="alert alert-warning">
                                            <i className="fa-solid fa-question-circle me-2"></i>
                                            Voulez-vous traiter cette transaction et l'ajouter à la comptabilité ?
                                        </div>
                                    )}
                                    {wizardTransaction.processed && !wizardTransaction.invoiceNumber && wizardTransaction.invoiceId && (
                                        <div className="alert alert-warning">
                                            <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                            Cette facture a été créée mais n'est pas encore validée. Souhaitez-vous la valider maintenant ?
                                        </div>
                                    )}
                                    {wizardTransaction.processed && wizardTransaction.invoiceNumber && (
                                        <div className="alert alert-success">
                                            <i className="fa-solid fa-check-circle me-2"></i>
                                            Cette transaction a été traitée et validée avec succès.
                                        </div>
                                    )}
                                    {hasMultiple && (
                                        <div className="alert alert-info">
                                            <i className="fa-solid fa-keyboard me-2"></i>
                                            <small>Astuce : Utilisez les touches ← → pour naviguer entre les transactions</small>
                                        </div>
                                    )}
                                </div>
                            )}

                            {wizardStep === 'invoice' && (
                                <div>
                                    <div className="alert alert-success">
                                        <i className="fa-solid fa-check-circle me-2"></i>
                                        Transaction traitée avec succès !
                                    </div>
                                    <div className="card mb-3">
                                        <div className="card-body text-center">
                                            <i className="fa-solid fa-file-invoice fa-3x text-primary mb-3"></i>
                                            <h5>Facture créée</h5>
                                            {processedInvoiceId ? (
                                                <>
                                                    <p className="text-muted">ID: <strong>{processedInvoiceId}</strong></p>
                                                    <a 
                                                        href={`${getParams()?.urlErp}compta/facture/card.php?facid=${processedInvoiceId}`} 
                                                        target='_blank' 
                                                        rel="noopener noreferrer"
                                                        className='btn btn-sm btn-info mt-2'
                                                    >
                                                        <i className="fa-solid fa-external-link-alt me-1"></i>
                                                        Voir la Facture
                                                    </a>
                                                </>
                                            ) : (
                                                <p className="text-danger">Aucun ID de facture reçu</p>
                                            )}
                                        </div>
                                    </div>
                                    {processedInvoiceId && (
                                        <div className="alert alert-info">
                                            <i className="fa-solid fa-question-circle me-2"></i>
                                            Souhaitez-vous valider et payer la facture maintenant ?
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            {/* Navigation buttons for multiple transactions */}
                            {hasMultiple && wizardStep === 'confirm' && (
                                <div className="me-auto">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary btn-sm me-2" 
                                        onClick={goToPreviousTransaction}
                                        disabled={!hasPrevious || wizardProcessing}
                                        title={previousTransaction ? `Remise: ${previousTransaction.note_private}${previousTransaction.invoiceNumber ? ' | Facture: ' + previousTransaction.invoiceNumber : ''}` : ''}
                                    >
                                        <i className="fa-solid fa-arrow-left me-1"></i>
                                        <span className="d-none d-md-inline">Précédent</span>
                                        {previousTransaction && (
                                            <small className="d-none d-lg-inline ms-1">({previousTransaction.note_private})</small>
                                        )}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary btn-sm" 
                                        onClick={goToNextTransaction}
                                        disabled={!hasNext || wizardProcessing}
                                        title={nextTransaction ? `Remise: ${nextTransaction.note_private}${nextTransaction.invoiceNumber ? ' | Facture: ' + nextTransaction.invoiceNumber : ''}` : ''}
                                    >
                                        <span className="d-none d-md-inline">Suivant</span>
                                        {nextTransaction && (
                                            <small className="d-none d-lg-inline me-1">({nextTransaction.note_private})</small>
                                        )}
                                        <i className="fa-solid fa-arrow-right ms-1"></i>
                                    </button>
                                </div>
                            )}

                            {wizardStep === 'confirm' && (
                                <>
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={closeWizard}
                                        disabled={wizardProcessing}
                                    >
                                        <i className="fa-solid fa-times me-2"></i>
                                        {hasMultiple ? 'Fermer' : 'Non, Annuler'}
                                    </button>
                                    {/* Show action buttons only if NOT processed or processed without invoice number */}
                                    {(!wizardTransaction.processed || !wizardTransaction.invoiceNumber) && (
                                        <>
                                            {hasMultiple && hasNext && (
                                                <button 
                                                    type="button" 
                                                    className="btn btn-warning" 
                                                    onClick={goToNextTransaction}
                                                    disabled={wizardProcessing}
                                                    title={nextTransaction ? `Passer à: ${nextTransaction.note_private}${nextTransaction.invoiceNumber ? ' | Facture: ' + nextTransaction.invoiceNumber : ''}` : ''}
                                                >
                                                    <i className="fa-solid fa-forward me-2"></i>
                                                    Passer
                                                    {nextTransaction && (
                                                        <small className="d-none d-lg-inline ms-1">({nextTransaction.note_private})</small>
                                                    )}
                                                </button>
                                            )}
                                            {!wizardTransaction.processed && (
                                                <button 
                                                    type="button" 
                                                    className="btn btn-success" 
                                                    onClick={processSingleTransaction}
                                                    disabled={wizardProcessing}
                                                >
                                                    {wizardProcessing ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Traitement...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-check me-2"></i>
                                                            Oui, Traiter
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                            {wizardTransaction.processed && wizardTransaction.invoiceId && !wizardTransaction.invoiceNumber && (
                                                <button 
                                                    type="button" 
                                                    className="btn btn-warning" 
                                                    onClick={handleWizardValidateInvoice}
                                                    disabled={wizardProcessing}
                                                >
                                                    {wizardProcessing ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Validation...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-check-double me-2"></i>
                                                            Valider la Facture
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </>
                                    )}
                                    {/* If processed with invoice number, show only navigation in hasMultiple */}
                                    {wizardTransaction.processed && wizardTransaction.invoiceNumber && hasMultiple && hasNext && (
                                        <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={goToNextTransaction}
                                            disabled={wizardProcessing}
                                            title={nextTransaction ? `Suivant: ${nextTransaction.note_private}${nextTransaction.invoiceNumber ? ' | Facture: ' + nextTransaction.invoiceNumber : ''}` : ''}
                                        >
                                            <i className="fa-solid fa-arrow-right me-2"></i>
                                            Suivant
                                            {nextTransaction && (
                                                <small className="d-none d-lg-inline ms-1">({nextTransaction.note_private})</small>
                                            )}
                                        </button>
                                    )}
                                </>
                            )}

                            {wizardStep === 'invoice' && (
                                <>
                                    {hasMultiple && (
                                        <div className="me-auto">
                                            <button 
                                                type="button" 
                                                className="btn btn-outline-secondary btn-sm me-2" 
                                                onClick={goToPreviousTransaction}
                                                disabled={!hasPrevious || wizardProcessing}
                                                title={previousTransaction ? `Remise: ${previousTransaction.note_private}${previousTransaction.invoiceNumber ? ' | Facture: ' + previousTransaction.invoiceNumber : ''}` : ''}
                                            >
                                                <i className="fa-solid fa-arrow-left me-1"></i>
                                                <span className="d-none d-md-inline">Précédent</span>
                                                {previousTransaction && (
                                                    <small className="d-none d-lg-inline ms-1">({previousTransaction.note_private})</small>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={hasMultiple && hasNext ? goToNextTransaction : closeWizard}
                                        disabled={wizardProcessing}
                                        title={hasMultiple && hasNext && nextTransaction ? `Passer à: ${nextTransaction.note_private}${nextTransaction.invoiceNumber ? ' | Facture: ' + nextTransaction.invoiceNumber : ''}` : ''}
                                    >
                                        <i className={`fa-solid fa-${hasMultiple && hasNext ? 'forward' : 'times'} me-2`}></i>
                                        {hasMultiple && hasNext ? 'Passer' : (processedInvoiceId ? 'Non, Terminer' : 'Fermer')}
                                        {hasMultiple && hasNext && nextTransaction && (
                                            <small className="d-none d-lg-inline ms-1">({nextTransaction.note_private})</small>
                                        )}
                                    </button>
                                    {processedInvoiceId && (
                                        <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={handleWizardValidateInvoice}
                                            disabled={wizardProcessing}
                                            title={hasMultiple && hasNext && nextTransaction ? `Valider et continuer avec: ${nextTransaction.note_private}` : ''}
                                        >
                                            {wizardProcessing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Validation...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-check-double me-2"></i>
                                                    Oui, Valider {hasMultiple && hasNext ? '& Suivant' : 'la Facture'}
                                                    {hasMultiple && hasNext && nextTransaction && (
                                                        <small className="d-none d-lg-inline ms-1">({nextTransaction.note_private})</small>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container mt-5" key={key}>
            <h1 className="h3 mb-3"><strong>CMI / VPS / Glovo</strong> Comptabilité</h1>
            {chosenCompany && (
                <div className="d-flex gap-2 mb-2 flex-wrap">
                    <button className='btn btn-warning' onClick={resetComponents}>Nouveau Fichier</button>
                    {selectedSystem === 'vps' && (
                        <button className='btn btn-outline-secondary' onClick={resetForNextVpsFile}>
                            Process
                        </button>
                    )}
                </div>
            )}
            <div>
                <Select

                    options={companyOptions}
                    value={chosenCompany}
                    onChange={setChosenCompany}
                    isDisabled={!!chosenCompany?.value}
                />
            </div>
            {chosenCompany && (
                <>
                    {!facture && (<>
                        <h2>Sélection du Système</h2>
                        <div className="mb-3">
                            <div className="form-check form-check-inline">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="systemSelection" 
                                    id="systemCmi" 
                                    value="cmi"
                                    checked={selectedSystem === 'cmi'}
                                    onChange={(e) => setSelectedSystem(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="systemCmi">
                                    CMI
                                </label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="systemSelection" 
                                    id="systemVps" 
                                    value="vps"
                                    checked={selectedSystem === 'vps'}
                                    onChange={(e) => setSelectedSystem(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="systemVps">
                                    VPS
                                </label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="systemSelection"
                                    id="systemGlovo"
                                    value="glovo"
                                    checked={selectedSystem === 'glovo'}
                                    onChange={(e) => setSelectedSystem(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="systemGlovo">
                                    Glovo
                                </label>
                            </div>
                            {isOfloristSelected() && (
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="systemSelection"
                                        id="systemCarrefour"
                                        value="carrefour"
                                        checked={selectedSystem === 'carrefour'}
                                        onChange={(e) => setSelectedSystem(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="systemCarrefour">
                                        Carrefour
                                    </label>
                                </div>
                            )}
                        </div>
                        <h2>Upload Excel File</h2>
                        <div className="mb-3">
                            <input key={fileInputKey} type="file" onChange={handleFileChange} accept=".xlsx,.xls,.csv" />
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <button className="btn btn-primary" onClick={handleUpload}>
                                Upload
                            </button>
                        </div>
                    </>)}

                    {facture && (
                        <div className="row mt-3 mb-3">
                            <div className="col-md">
                                <div className="card border-info">
                                    <div className="card-body text-center">
                                        <h6 className="text-muted">Total Transactions</h6>
                                        <h3 className="text-info">{facture.length}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md">
                                <div className="card border-success">
                                    <div className="card-body text-center">
                                        <h6 className="text-muted">Traitées</h6>
                                        <h3 className="text-success">{facture.filter(f => isVpsEffectivelyProcessed(f)).length}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md">
                                <div className="card border-warning">
                                    <div className="card-body text-center">
                                        <h6 className="text-muted">En Attente</h6>
                                        <h3 className="text-warning">{facture.filter(f => !isVpsEffectivelyProcessed(f)).length}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md">
                                <div className="card border-primary">
                                    <div className="card-body text-center">
                                        <h6 className="text-muted">Montant Total</h6>
                                        <h3 className="text-primary">{formatCurrency(getDisplayTotalAmount())}</h3>
                                    </div>
                                </div>
                            </div>
                            {hasVpsPaidSummary && (
                                <div className="col-md">
                                    <div className="card border-dark">
                                        <div className="card-body text-center">
                                            <h6 className="text-muted">Paid</h6>
                                            <h3 className="text-dark">{formatCurrency(vpsSummary.netMarchand)}</h3>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {facture && facture.some(f => f.isVps) && (
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <div className="card border-secondary">
                                    <div className="card-body">
                                        <h6 className="text-muted mb-2">
                                            <i className="fa-solid fa-credit-card me-2"></i>
                                            CMI Transactions
                                        </h6>
                                        <div className="d-flex justify-content-between">
                                            <span>Groupes: <strong>{facture.filter(f => !f.isVps).length}</strong></span>
                                            <span>Montant: <strong>{facture.filter(f => !f.isVps).reduce((acc, a) => acc + getTransactionTotal(a), 0).toFixed(2)} MAD</strong></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card border-secondary">
                                    <div className="card-body">
                                        <h6 className="text-muted mb-2">
                                            <i className="fa-solid fa-server me-2"></i>
                                            VPS Transactions
                                        </h6>
                                        <div className="d-flex justify-content-between">
                                            <span>Groupes: <strong>{facture.filter(f => f.isVps).length}</strong></span>
                                            <span>Montant: <strong>{formatCurrency(Number(vpsSummary?.totalCreditMerchant) || facture.filter(f => f.isVps).reduce((acc, a) => acc + getTransactionTotal(a), 0))}</strong></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {facture && (
                        <div className="d-flex gap-2 mb-3">
                            <button 
                                className="btn btn-primary" 
                                onClick={startWizardForAll}
                            >
                                <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                                Assistant pour Toutes ({facture.length})
                            </button>
                            {facture.filter(f => !isVpsEffectivelyProcessed(f) && !f.isVps).length > 0 && (
                                <button 
                                    className="btn btn-success" 
                                    onClick={handleActionButtonClick}
                                    disabled={selectedOrders.length === 0}
                                >
                                    <i className="fa-solid fa-check-double me-2"></i>
                                    Traiter les Sélectionnées {selectedSystem === 'carrefour' ? 'Carrefour' : selectedSystem === 'glovo' ? 'Glovo' : 'CMI'} ({selectedOrders.filter(orderId => {
                                        const item = facture.find(f => f.remiseId === orderId);
                                        return item && !item.isVps;
                                    }).length})
                                </button>
                            )}
                            {facture.filter(f => !isVpsEffectivelyProcessed(f) && f.isVps).length > 1 && (
                                <button 
                                    className="btn btn-info" 
                                    onClick={mergeSelectedVps}
                                    disabled={selectedOrders.filter(orderId => {
                                        const item = facture.find(f => f.remiseId === orderId);
                                        return item && item.isVps && !isVpsEffectivelyProcessed(item);
                                    }).length < 2}
                                >
                                    <i className="fa-solid fa-object-group me-2"></i>
                                    Fusionner VPS Sélectionnés ({selectedOrders.filter(orderId => {
                                        const item = facture.find(f => f.remiseId === orderId);
                                        return item && item.isVps && !isVpsEffectivelyProcessed(item);
                                    }).length})
                                </button>
                            )}
                        </div>
                    )}



                    {selectedSystem === 'vps' && (facture?.some(f => f.isVps) || vpsDebitGroups.length > 0) && (
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <ul className="nav nav-pills">
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${vpsActiveTab === 'credits' ? 'active' : ''}`}
                                        onClick={() => setVpsActiveTab('credits')}
                                    >
                                        <i className="fa-solid fa-arrow-trend-up me-2"></i>
                                        Crédits (C) ({facture?.filter(f => f.isVps).length || 0})
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${vpsActiveTab === 'debits' ? 'active bg-danger' : 'text-danger'}`}
                                        onClick={() => setVpsActiveTab('debits')}
                                    >
                                        <i className="fa-solid fa-rotate-left me-2"></i>
                                        Débits / Avoirs (D) ({vpsDebitGroups.length})
                                    </button>
                                </li>
                            </ul>
                            {vpsDebitGroups.length > 0 && (
                                <span className="badge bg-danger fs-6">
                                    Total D: {formatCurrency(vpsDebitGroups.reduce((sum, group) => sum + Number(group.totalDebit || 0), 0))}
                                </span>
                            )}
                        </div>
                    )}

                    {vpsActiveTab === 'credits' && (
                    <table className=' mt-2 table table-responsive'>
                        <thead className='bg-warning'>
                            <tr >
                                <th> <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={handleSelectAllChange}
                                />
                                </th>
                                <th>Client</th>
                                <th>Remise Numéro</th>
                                <th>Nombre de transactions</th>
                                <th>Date de facture</th>
                                <th>Nom Produit</th>
                                <th>Montant Total</th>
                                <th>Processed</th>
                                <th>Facture Numéro</th>
                                <th>Type</th>
                                <th>Actions</th>

                            </tr>
                        </thead>

                        {(facture) ? (<><tbody>
                            {facture.sort((a, b) => {
                                // Handle VPS transactions (no socid)
                                if (a.isVps && b.isVps) return 0;
                                if (a.isVps) return 1;
                                if (b.isVps) return -1;
                                return (a.socid || 0) - (b.socid || 0);
                            }).map((f, i) => {
                                const isExpanded = expandedRows.includes(f.remiseId);
                                const vpsStatus = getVpsStage(f);
                                const rowIsProcessed = isVpsEffectivelyProcessed(f);
                                return (
                                    <React.Fragment key={f.remiseId}>
                                        <tr className={(isVpsGreenRow(f)) ? ((i % 2 == 0) ? "bg-success" : "bg-success-l") : ""}>
                                            <td><input
                                                type="checkbox"
                                                checked={selectedOrders.includes(f.remiseId)}
                                                disabled={rowIsProcessed}
                                                onChange={() => handleCheckboxChange(f.remiseId)}
                                            />
                                            </td>
                                            <td>
                                                {f.isVps ? (
                                                    f.isMerged ? (
                                                        <div>
                                                            <span className="badge bg-info">VPS</span>
                                                            <span className="badge bg-warning ms-1">
                                                                <i className="fa-solid fa-object-group me-1"></i>
                                                                Fusionné
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="badge bg-info">VPS</span>
                                                    )
                                                ) : (
                                                    f.isCarrefour ? (
                                                        <span className="badge bg-info">CARREFOUR</span>
                                                    ) : f.isGlovo ? (
                                                        <span className="badge bg-dark">GLOVO</span>
                                                    ) : (
                                                        <span className={`badge bg-${getParams()?.site == f.socid ? "primary" : "warning"}`}>
                                                            {getParams()?.site == f.socid ? "SITE" : "TPE"}
                                                        </span>
                                                    )
                                                )}
                                            </td>
                                            <td>
                                                <button 
                                                    className="btn btn-link btn-sm text-decoration-none p-0"
                                                    onClick={() => toggleRowExpansion(f.remiseId)}
                                                    title="Voir les détails des transactions"
                                                >
                                                    <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                                                    {f.note_private}
                                                </button>
                                            </td>
                                            <td>{(f.isCarrefour || f.isGlovo) ? Number.parseInt(String(f.lines.reduce((acc, line) => acc + (Number(line?.trx) || 0), 0)), 10) : Number.parseInt(String(f.lines[0].trx), 10)}</td>
                                            <td>{f.datef}</td>
                                            <td>{(f.isCarrefour || f.isGlovo) ? `Lignes: ${f.lines.length}` : f.lines[0].desc}</td>
                                            <td><span className='badge bg-dark text-light '>{getTransactionTotal(f).toFixed(2)} <small> {f.lines[0].currency}</small></span></td>
                                            <td>
                                                {rowIsProcessed ? (
                                                    <span className={`badge text-uppercase ${
                                                        isVpsGreenRow(f) ? 'bg-success' : 'bg-secondary'
                                                    }`}>
                                                        {vpsStatus || 'traitée'}
                                                    </span>
                                                ) : (
                                                    <i className="fa-solid fa-hourglass"></i>
                                                )}
                                            </td>
                                            <td>{f?.invoiceNumber}</td>
                                            <td>{f.isCarrefour ? 'carrefour' : f.isGlovo ? 'glovo' : f.lines[0].product_type}</td>
                                            <td>
                                                {!rowIsProcessed ? (
                                                    f.isVps ? (
                                                        <span className="badge bg-secondary">
                                                            <i className="fa-solid fa-clock me-1"></i>
                                                            VPS - {vpsStatus}
                                                        </span>
                                                    ) : (
                                                        <button 
                                                            className='btn btn-sm btn-primary me-2' 
                                                            onClick={() => startWizard(f)}
                                                            title="Démarrer l'assistant de traitement"
                                                        >
                                                            <i className="fa-solid fa-wand-magic-sparkles me-1"></i>
                                                            Wizard
                                                        </button>
                                                    )
                                                ) : (
                                                    <>
                                                        {f?.invoiceId && (
                                                            <a href={`${getParams()?.urlErp}compta/facture/card.php?facid=${f?.invoiceId}`} target='_blank' className='btn btn-sm btn-info me-2'>
                                                                <i className="fa-solid fa-external-link-alt me-1"></i>
                                                                {f?.invoiceId}
                                                            </a>
                                                        )}
                                                        {!f?.invoiceNumber && f?.invoiceId && (
                                                            <button className='btn btn-sm btn-warning' onClick={() => validateInvoice(f?.invoiceId)}>
                                                                <i className="fa-solid fa-check me-1"></i>
                                                                Valider ({vpsStatus})
                                                            </button>
                                                        )}
                                                        {f?.invoiceNumber && (
                                                            <span className="badge bg-success">
                                                                <i className="fa-solid fa-check-circle me-1"></i>
                                                                Validé: {f.invoiceNumber}
                                                            </span>
                                                        )}
                                                        {f.isVps && !f.invoiceNumber && f.invoiceId && (
                                                            <span className="badge bg-info ms-2">
                                                                Étape: {vpsStatus}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                        {isExpanded && (f.rawTransactions || f.isGlovo) && (
                                            <tr>
                                                <td colSpan="11" className="p-0">
                                                    <div className="bg-light p-3 border-start border-4 border-info">
                                                        {f.isGlovo && (() => {
                                                            const glovoTotals = calculateGlovoFinancials(f);
                                                            const adBudgetEntries = getGlovoAdBudgetEntries(f);
                                                            return (
                                                                <div className="mb-3">
                                                                    <h6 className="mb-3">
                                                                        <i className="fa-solid fa-list me-2"></i>
                                                                        Détails Glovo
                                                                    </h6>
                                                                    <div className="row g-3 align-items-end">
                                                                        <div className="col-md-6">
                                                                            <label className="form-label d-flex justify-content-between align-items-center">
                                                                                <span>Ad budget HT</span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={() => addGlovoBudgetEntry(f.remiseId)}
                                                                                >
                                                                                    <i className="fa-solid fa-plus me-1"></i>
                                                                                    Ajouter
                                                                                </button>
                                                                            </label>
                                                                            <div className="d-flex flex-column gap-2">
                                                                                {adBudgetEntries.map((entry, entryIndex) => (
                                                                                    <div className="input-group" key={`table-ad-budget-${entryIndex}`}>
                                                                                        <input
                                                                                            type="number"
                                                                                            step="0.01"
                                                                                            min="0"
                                                                                            className="form-control"
                                                                                            value={entry}
                                                                                            onChange={(e) => updateGlovoBudgetEntry(f.remiseId, entryIndex, e.target.value)}
                                                                                        />
                                                                                        {adBudgetEntries.length > 1 && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-outline-danger"
                                                                                                onClick={() => removeGlovoBudgetEntry(f.remiseId, entryIndex)}
                                                                                            >
                                                                                                <i className="fa-solid fa-trash"></i>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-3">
                                                                            <label className="form-label">Dispute TTC</label>
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                min="0"
                                                                                className="form-control"
                                                                                value={Number(f?.glovoSummary?.disputeTtc || 0)}
                                                                                onChange={(e) => updateGlovoTransaction(f.remiseId, 'disputeTtc', e.target.value)}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="small text-muted">
                                                                                <div>Base commission TTC: <strong>{glovoTotals.baseCommissionTtc.toFixed(2)} MAD</strong></div>
                                                                                <div>Commission facturée HT: <strong>{glovoTotals.commissionBilledHt.toFixed(2)} MAD</strong></div>
                                                                                <div>A TTC: <strong>{glovoTotals.aTtc.toFixed(2)} MAD</strong></div>
                                                                                <div>B TTC (hors dispute): <strong>{glovoTotals.bTtc.toFixed(2)} MAD</strong></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="table-responsive mt-3">
                                                                        <table className="table table-sm table-bordered">
                                                                            <thead className="table-secondary">
                                                                                <tr>
                                                                                    <th>Produit</th>
                                                                                    <th>Qté</th>
                                                                                    <th>PU HT</th>
                                                                                    <th>Total HT</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {f.lines.map((line, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td>{line?.desc || line?.label}</td>
                                                                                        <td>{Number(line?.qty || 1)}</td>
                                                                                        <td>{Number(line?.subprice || 0).toFixed(2)}</td>
                                                                                        <td>{((Number(line?.subprice || 0) * Number(line?.qty || 1))).toFixed(2)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                        {f.rawTransactions && (
                                                        <>
                                                        <h6 className="mb-3">
                                                            <i className="fa-solid fa-list me-2"></i>
                                                            Détails des Transactions ({f.rawTransactions.length})
                                                        </h6>
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-hover">
                                                                <thead className="table-secondary">
                                                                    <tr>
                                                                        <th>Date Transaction</th>
                                                                        <th>Heure</th>
                                                                        <th>Système</th>
                                                                        <th>Carte</th>
                                                                        <th>N° Autorisation</th>
                                                                        <th>Montant Brut TTC</th>
                                                                        <th>Commission</th>
                                                                        <th>TVA Comm.</th>
                                                                        <th>Montant Net TTC</th>
                                                                        <th>Numéro de remise</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {f.rawTransactions.map((trx, idx) => (
                                                                        <tr key={idx}>
                                                                            <td>{trx.dateTransaction}</td>
                                                                            <td><small>{trx.heure}</small></td>
                                                                            <td>
                                                                                <span className="badge bg-secondary">{trx.systeme}</span>
                                                                            </td>
                                                                            <td><code className="small">{trx.numeroCarte}</code></td>
                                                                            <td><small>{trx.numeroAutorisation}</small></td>
                                                                            <td>
                                                                                <span className="badge bg-primary">
                                                                                    {parseFloat(trx.montantBrutTTC).toFixed(2)} {trx.devise}
                                                                                </span>
                                                                            </td>
                                                                            <td><small>{parseFloat(trx.commission || 0).toFixed(2)}</small></td>
                                                                            <td><small>{parseFloat(trx.tvaCommission || 0).toFixed(2)}</small></td>
                                                                            <td>
                                                                                <strong className="text-success">
                                                                                    {parseFloat(trx.montantNetTTC).toFixed(2)} {trx.devise}
                                                                                </strong>
                                                                            </td>
                                                                            <td><span className="badge bg-info">{f.note_private}</span></td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot className="table-light">
                                                                    <tr>
                                                                        <td colSpan="5" className="text-end fw-bold">Totaux:</td>
                                                                        <td>
                                                                            <span className="badge bg-primary">
                                                                                {f.rawTransactions.reduce((sum, t) => sum + parseFloat(t.montantBrutTTC), 0).toFixed(2)} {f.lines[0].currency}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            {f.rawTransactions.reduce((sum, t) => sum + parseFloat(t.commission || 0), 0).toFixed(2)}
                                                                        </td>
                                                                        <td>
                                                                            {f.rawTransactions.reduce((sum, t) => sum + parseFloat(t.tvaCommission || 0), 0).toFixed(2)}
                                                                        </td>
                                                                        <td>
                                                                            <strong className="text-success">
                                                                                {f.rawTransactions.reduce((sum, t) => sum + parseFloat(t.montantNetTTC), 0).toFixed(2)} {f.lines[0].currency}
                                                                            </strong>
                                                                        </td>
                                                                        <td></td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                        </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                            }
                        </tbody>
                            <tfoot>
                                <tr>
                                    <td className='text-end' colSpan={7}>
                                        <h1>TOTAL : <span className='badge bg-warning'>{formatCurrency(getDisplayTotalAmount())}</span> </h1>
                                    </td>
                                </tr>
                            </tfoot>
                        </>
                        ) : ''}



                    </table>
                    )}

                    {selectedSystem === 'vps' && vpsActiveTab === 'debits' && (
                        <div className="card border-danger mt-2">
                            <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center">
                                <span>
                                    <i className="fa-solid fa-rotate-left me-2"></i>
                                    Transactions D - Remboursements / Avoirs
                                </span>
                                <strong>{formatCurrency(vpsDebitGroups.reduce((sum, group) => sum + Number(group.totalDebit || 0), 0))}</strong>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Table</th>
                                            <th>Date</th>
                                            <th>Transactions</th>
                                            <th>Montant D</th>
                                            <th>Statut avoir</th>
                                            <th>Détails</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vpsDebitGroups.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted py-4">
                                                    Aucune table D dans ce fichier.
                                                </td>
                                            </tr>
                                        ) : vpsDebitGroups.map((group) => {
                                            const isExpanded = expandedRows.includes(group.remiseId);
                                            const matchedCount = group.rawTransactions.filter(trx => trx.matchedCreditTransactionId || trx.refundInvoiceId).length;
                                            return (
                                                <React.Fragment key={group.remiseId}>
                                                    <tr>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm text-danger text-decoration-none p-0"
                                                                onClick={() => toggleRowExpansion(group.remiseId)}
                                                            >
                                                                <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                                                                {group.note_private}
                                                            </button>
                                                        </td>
                                                        <td>{group.datef}</td>
                                                        <td>{group.transactionCount}</td>
                                                        <td>
                                                            <span className="badge bg-danger">
                                                                {formatCurrency(group.totalDebit)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {matchedCount > 0 ? (
                                                                <span className="badge bg-success">{matchedCount} matchée(s)</span>
                                                            ) : (
                                                                <span className="badge bg-warning text-dark">À matcher</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="text-muted small">
                                                                STAN / carte / montant pour trouver la facture C
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={6} className="p-0">
                                                                <div className="table-responsive bg-light p-3 border-start border-4 border-danger">
                                                                    <table className="table table-sm table-bordered mb-0">
                                                                        <thead className="table-secondary">
                                                                            <tr>
                                                                                <th>Ligne</th>
                                                                                <th>Heure</th>
                                                                                <th>Carte</th>
                                                                                <th>STAN</th>
                                                                                <th>N° Autorisation</th>
                                                                                <th>Montant brut</th>
                                                                                <th>Montant D</th>
                                                                                <th>Avoir</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {group.rawTransactions.map((trx, index) => (
                                                                                <tr key={`${group.remiseId}-${index}`}>
                                                                                    <td>{trx.sourceSheetRow || '-'}</td>
                                                                                    <td>{trx.heure}</td>
                                                                                    <td><code>{trx.numeroCarte}</code></td>
                                                                                    <td>{trx.stan}</td>
                                                                                    <td>{trx.numeroAutorisation}</td>
                                                                                    <td>{formatCurrency(trx.montantBrutTTC)}</td>
                                                                                    <td><strong className="text-danger">{formatCurrency(trx.montantDebitTTC)}</strong></td>
                                                                                    <td>
                                                                                        {trx.refundInvoiceNumber ? (
                                                                                            <span className="badge bg-success">{trx.refundInvoiceNumber}</span>
                                                                                        ) : trx.refundInvoiceId ? (
                                                                                            <span className="badge bg-info">{trx.refundInvoiceId}</span>
                                                                                        ) : (
                                                                                            <span className="badge bg-secondary">Non créé</span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Wizard Modal */}
            {renderWizardModal()}

            {reglementDateModal.open && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-warning">
                                <h5 className="modal-title">
                                    <i className="fa-solid fa-calendar-days me-2"></i>
                                    Date de reglement
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => closeReglementDateModal(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <label className="form-label">Choisissez la date de reglement</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    min={reglementDateModal.minDate}
                                    value={reglementDateModal.value}
                                    onChange={(e) => setReglementDateModal((prev) => ({ ...prev, value: e.target.value }))}
                                />
                                <small className="text-muted">
                                    Date minimale: {formatIsoDateForDisplay(reglementDateModal.minDate)}
                                </small>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => closeReglementDateModal(null)}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        if (!reglementDateModal.value) {
                                            toast.error("Veuillez choisir une date de reglement.");
                                            return;
                                        }
                                        if (reglementDateModal.value < reglementDateModal.minDate) {
                                            toast.error("La date de reglement ne peut pas etre anterieure a la date de facture.");
                                            return;
                                        }
                                        closeReglementDateModal(reglementDateModal.value);
                                    }}
                                >
                                    Confirmer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    );
}

export default CmiAccounting;
